import * as XLSX from 'xlsx';
import {
  extractMetadataOptions,
  findLookupById,
  findLookupByName,
  parseCsvText,
} from '../assigner-bulk-upload/assigner-bulk-upload.util';
import {
  createDefaultFpBulkSelections,
  createEmptyEntitySelections,
  FP_BULK_MAX_ROWS,
  FP_BULK_NET_QTY_UNITS,
  FP_BULK_PRODUCT_FORMS,
  FP_BULK_PRODUCT_TYPES,
  FP_BULK_SHELF_LIFE_UNITS,
  FP_BULK_STATUS_OPTIONS,
  FP_BULK_TEMPLATE_HEADERS,
  FP_BULK_TEMPLATE_SAMPLE_ROW,
  FpBulkClaimDraft,
  FpBulkEntitySelections,
  FpBulkFieldError,
  FpBulkIngredientDraft,
  FpBulkLookups,
  FpBulkLookupOption,
  FpBulkNutrientItem,
  FpBulkParseResult,
  FpBulkParsedText,
  FpBulkPreviewRow,
  FpBulkRowSelections,
  FpBulkServingMeasurement,
  FpBulkSkuDraft,
} from './super-admin-finish-product-bulk-upload.models';
import { syncFpBulkAllergensFromIngredients } from './super-admin-finish-product-bulk-ingredient-enricher';

function normalizeHeader(header: string): string {
  return String(header || '')
    .replace(/^\uFEFF/, '')
    .trim()
    .replace(/[\s_-]+/g, '')
    .toLowerCase();
}

function cell(row: Record<string, string>, ...aliases: string[]): string {
  for (const alias of aliases) {
    const key = normalizeHeader(alias);
    const match = Object.keys(row).find((k) => normalizeHeader(k) === key);
    if (match != null && row[match] != null) {
      return String(row[match]).trim();
    }
  }
  return '';
}

function csvEscape(value: string): string {
  const raw = String(value ?? '');
  if (/[",\n\r]/.test(raw)) {
    return `"${raw.replace(/"/g, '""')}"`;
  }
  return raw;
}

export function downloadSuperAdminFinishProductTemplate(
  format: 'csv' | 'xlsx' = 'csv'
): void {
  const headers = [...FP_BULK_TEMPLATE_HEADERS];
  const sample = FP_BULK_TEMPLATE_HEADERS.map((h) => FP_BULK_TEMPLATE_SAMPLE_ROW[h] ?? '');
  const baseName = 'SuperAdmin_RDMS_FinishProduct_Template';

  if (format === 'xlsx') {
    const sheet = XLSX.utils.aoa_to_sheet([headers, sample]);
    sheet['!cols'] = headers.map((h) => ({ wch: Math.min(36, Math.max(14, h.length + 2)) }));
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'FinishProducts');
    const guide = XLSX.utils.aoa_to_sheet([
      ['RDMS Finish Product Bulk Upload — Template Guide'],
      [''],
      ['Required columns', 'None — all columns optional. Empty product name imports as Untitled Product (Row N).'],
      ['Formats', 'Upload .xlsx, .xls, or .csv. Full RDMS export files are also accepted.'],
      ['Any form OK', 'No fixed format required. Free text ingredients/macros/claims/SKU are accepted. Master fields accept ID and/or Name.'],
      ['Master match', 'When a value matches master it fills the dropdown; otherwise the text is still saved.'],
      ['Entities', 'Use ManufacturedBy* columns, or export-style Manufacturers N Type/Name/Address/License.'],
      ['SKU', 'SKUDetails free text OR Name|NetWt|MRP|... — both work.'],
      ['Lists', 'Use commas/semicolons freely. Optional structured form: Type|Text;;Type|Text'],
    ]);
    guide['!cols'] = [{ wch: 28 }, { wch: 90 }];
    XLSX.utils.book_append_sheet(book, guide, 'Guide');
    XLSX.writeFile(book, `${baseName}.xlsx`);
    return;
  }

  const headerLine = headers.join(',');
  const sampleLine = sample.map((v) => csvEscape(v)).join(',');
  const csvContent = `\uFEFF${headerLine}\n${sampleLine}\n`;
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${baseName}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/** Extract amount + unit token from free text (e.g. "800 g", "Net quantity 400 g"). */
function extractQtyAndUnit(text: string): { amount: string; unit: string } | null {
  const raw = String(text || '').trim();
  if (!raw) return null;
  const m = raw.match(
    /(\d+(?:\.\d+)?)\s*(g|gm|grams?|kg|kgs?|ml|l|ltr|liters?|litres?|mg|mcg|µg|ug|%)\b/i
  );
  if (!m) return null;
  return { amount: m[1], unit: normalizeUnitToken(m[2]) };
}

/** Normalize unit abbreviations used in Excel / labels to a stable token. */
function normalizeUnitToken(unit: string): string {
  const u = String(unit || '').trim().toLowerCase();
  if (!u) return '';
  if (u === '%' || u === 'percent' || u === 'percentage') return '%';
  if (u === 'g' || u === 'gm' || u === 'gram' || u === 'grams') return 'g';
  if (u === 'kg' || u === 'kgs' || u === 'kilogram' || u === 'kilograms') return 'kg';
  if (u === 'ml' || u === 'milliliter' || u === 'millilitre' || u === 'milliliters') return 'ml';
  if (u === 'l' || u === 'ltr' || u === 'liter' || u === 'litre' || u === 'liters' || u === 'litres') return 'L';
  if (u === 'mg' || u === 'milligram' || u === 'milligrams') return 'mg';
  if (u === 'mcg' || u === 'µg' || u === 'ug' || u === 'microgram' || u === 'micrograms') return 'mcg';
  if (u === 'kcal' || u === 'calorie' || u === 'calories' || u === 'kilocalorie' || u === 'kilocalories') return 'kcal';
  if (u === 'kj' || u === 'kilojoule' || u === 'kilojoules') return 'kj';
  if (u === 'iu') return 'IU';
  return unit.trim();
}

/**
 * True structured SKU cell: multiple ;; rows, or Name|NetWeight|MRP|… with 3+ pipes,
 * or a Unit column present at index 6. Free-text like
 * "Parle-G … 800 g Pouch | Net quantity 800 g" is NOT structured.
 */
function isStructuredSkuCell(text: string): boolean {
  if (text.includes(';;')) return true;
  const pieces = text.split('|').map((p) => p.trim()).filter(Boolean);
  if (pieces.length >= 3) return true;
  if (pieces.length === 2) {
    const second = pieces[1];
    // "Net quantity 800 g" / pack blurb — free text, not MRP/unit columns
    if (/^net\s*quantity\b/i.test(second)) return false;
    if (/^\d+(\.\d+)?\s*(g|gm|kg|ml|l|mg)?\s*$/i.test(second)) return true; // Name|500g
  }
  return false;
}

function enrichSkuDraftFromText(sku: FpBulkSkuDraft): FpBulkSkuDraft {
  const blob = [sku.name, sku.netWeight, sku.unitName].filter(Boolean).join(' ');
  const qty = extractQtyAndUnit(blob);
  if (qty) {
    if (!sku.unitName) sku.unitName = qty.unit;
    const nw = String(sku.netWeight || '').trim();
    if (!nw || /net\s*quantity/i.test(nw) || /[a-zA-Z%]/.test(nw)) {
      sku.netWeight = qty.amount;
    }
  }
  if (sku.unitName) sku.unitName = normalizeUnitToken(sku.unitName);
  return sku;
}

export function parseSkuRaw(raw: string): FpBulkSkuDraft[] {
  const text = String(raw || '').trim();
  if (!text) return [];

  // Structured: Name|NetWeight|MRP|Mfg|Mkt|Pkg|Unit|Country|Barcode;;...
  if (isStructuredSkuCell(text)) {
    return text
    .split(';;')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const pieces = part.split('|').map((p) => p.trim());
        const draft: FpBulkSkuDraft = {
        name: pieces[0] || '',
        netWeight: pieces[1] || '',
        mrp: pieces[2] || '',
        manufacturedBy: pieces[3] || '',
        marketedBy: pieces[4] || '',
        packagedBy: pieces[5] || '',
        unitId: null,
          unitName: pieces[6] || null,
          countryId: null,
          countryName: pieces[7] || null,
          productImage: null,
          productBarcode: pieces[8] || null,
        };
        return enrichSkuDraftFromText(draft);
      })
      .filter((s) => s.name || s.netWeight || s.mrp);
  }

  // Free text: "Parle-G Original Gluco Biscuits, 800 g Pouch | Net quantity 800 g"
  // Prefer the segment after "|" when it looks like net quantity.
  const pipeParts = text.split('|').map((p) => p.trim()).filter(Boolean);
  const namePart = pipeParts[0] || text;
  const qtyPart = pipeParts.length > 1 ? pipeParts[pipeParts.length - 1] : text;
  const qty = extractQtyAndUnit(qtyPart) || extractQtyAndUnit(text);
  return [
    enrichSkuDraftFromText({
      name: namePart.slice(0, 200),
      netWeight: qty ? qty.amount : '',
      mrp: '',
      manufacturedBy: '',
      marketedBy: '',
      packagedBy: '',
      unitId: null,
      unitName: qty ? qty.unit : null,
        countryId: null,
        countryName: null,
        productImage: null,
        productBarcode: null,
    }),
  ];
}

export function parseServingMeasurementsRaw(raw: string): FpBulkServingMeasurement[] {
  const text = String(raw || '').trim();
  if (!text) return [];

  if (text.includes('|') || text.includes(';;')) {
    return text
      .split(';;')
      .map((part) => part.trim())
      .filter(Boolean)
      .map((part) => {
        const pieces = part.split('|').map((p) => p.trim());
        const toNum = (v: string) => {
          const n = Number(v);
          return Number.isFinite(n) ? n : null;
        };
        return {
          servingSize: pieces[0] || null,
          qty: toNum(pieces[1]),
          amount: toNum(pieces[1]),
          unit: pieces[2] || null,
          servingMeasure: pieces[2] || null,
          calories: toNum(pieces[3]),
          carbs: toNum(pieces[4]),
          protein: toNum(pieces[5]),
          fat: toNum(pieces[6]),
          fiber: toNum(pieces[7]),
          uomId: null,
        } as FpBulkServingMeasurement;
      })
      .filter((s) => s.servingSize || s.qty != null);
  }

  // Free text: "15 g serving" / "24 g (approximately 2 scoops)"
  const qty = text.match(/(\d+(?:\.\d+)?)\s*(g|kg|ml|l|mg|scoop|scoops)?/i);
  return [
    {
      servingSize: text.slice(0, 200),
      qty: 1,
      amount: qty ? Number(qty[1]) : null,
      unit: qty?.[2] || 'g',
      servingMeasure: qty?.[2] || 'g',
      calories: 0,
      carbs: 0,
      protein: 0,
      fat: 0,
      fiber: 0,
      uomId: null,
    } as FpBulkServingMeasurement,
  ];
}

export function parseClaimsRaw(raw: string): FpBulkClaimDraft[] {
  const text = String(raw || '').trim();
  if (!text) return [];

  if (text.includes('|') || text.includes(';;')) {
    return text
      .split(';;')
      .map((part) => part.trim())
      .filter(Boolean)
      .map((part) => {
        const pipe = part.indexOf('|');
        if (pipe >= 0) {
          return {
            claimTypeId: '',
            claimTypeName: part.slice(0, pipe).trim(),
            claimText: part.slice(pipe + 1).trim(),
            selectedTagIds: [],
            selectedTagNames: [],
          } as FpBulkClaimDraft;
        }
        return {
          claimTypeId: '',
          claimTypeName: '',
          claimText: part,
          selectedTagIds: [],
          selectedTagNames: [],
        } as FpBulkClaimDraft;
      })
      .filter((c) => c.claimText || c.claimTypeName);
  }

  // Free text / sentence list — split on ; when present, else one claim.
  // Do NOT invent a fake claimTypeName like "Imported" (breaks category dropdown).
  const parts =
    text.includes(';')
      ? text.split(';').map((p) => p.trim().replace(/\.+$/, '')).filter((p) => p.length > 1)
      : text.length > 180
        ? text.split(/\.\s+/).map((p) => p.trim()).filter((p) => p.length > 3)
        : [text.replace(/\.+$/, '')];

  return parts.map(
    (claimText) =>
      ({
        claimTypeId: '',
        claimTypeName: '',
        claimText,
        selectedTagIds: [],
        selectedTagNames: [],
      }) as FpBulkClaimDraft
  );
}

export function parseIngredientsRaw(raw: string): FpBulkIngredientDraft[] {
  const text = String(raw || '').trim();
  if (!text) return [];

  // Re-exported finish-product ingredient dumps look like:
  // "Finish Product ID: 894; Display Name: maida 68%, sugar, palm oil; Sort Order: 0|…"
  // Do not treat those pipes as Name|Amount|Unit.
  if (looksLikeIngredientExportDump(text)) {
    return finalizeIngredientDrafts(parseIngredientExportDump(text));
  }

  // Structured: Name|Amount|Unit;;Name|Amount|Unit
  if (isStructuredIngredientCell(text)) {
    const structured = text
    .split(';;')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const pieces = part.split('|').map((p) => p.trim());
      return {
        name: pieces[0] || '',
        amount: pieces[1] || '',
        unit: pieces[2] || '',
      };
    })
    .filter((i) => i.name);
    return finalizeIngredientDrafts(structured);
  }

  // Free-text label list: "wheat flour 68%, sugar, palm oil; milk solids"
  return finalizeIngredientDrafts(
    splitIngredientFreeText(text).map((part) => draftFromIngredientPhrase(part))
  );
}

function looksLikeIngredientExportDump(text: string): boolean {
  const t = String(text || '');
  if (/finish\s*product\s*id\s*:/i.test(t)) return true;
  return /display\s*name\s*:/i.test(t) && /(?:sort\s*order|is\s*deleted|created\s*at)\s*:/i.test(t);
}

/** True Name|Amount|Unit rows — not free text / export blobs that happen to contain `|`. */
function isStructuredIngredientCell(text: string): boolean {
  if (looksLikeIngredientExportDump(text)) return false;
  if (text.includes(';;')) return true;
  if (!text.includes('|')) return false;

  const segments = text
    .split(';;')
    .map((s) => s.trim())
    .filter(Boolean);
  if (!segments.length) return false;

  return segments.every((seg) => {
    const pieces = seg.split('|').map((p) => p.trim());
    if (pieces.length < 2 || pieces.length > 4) return false;
    const amount = pieces[1] || '';
    if (!amount) return true;
    if (/^\d+(\.\d+)?$/.test(amount)) return true;
    // Amount column should stay short; long blobs are free-text misreads.
    return amount.length <= 24 && !/;/.test(amount);
  });
}

function parseIngredientExportDump(text: string): FpBulkIngredientDraft[] {
  const records = text
    .split('|')
    .map((s) => s.trim())
    .filter(Boolean);
  const drafts: FpBulkIngredientDraft[] = [];

  for (const rec of records) {
    const nameMatch = rec.match(/display\s*name\s*:\s*([^;]*)/i);
    if (!nameMatch) continue;

    const displayName = String(nameMatch[1] || '')
      .trim()
      .replace(/:+\s*$/, '')
      .trim();
    if (!displayName || isJunkIngredientName(displayName)) continue;

    const pctMatch = rec.match(/percentage\s*:\s*(\d+(?:\.\d+)?)/i);
    const percentage = pctMatch?.[1] || '';

    const parts = splitIngredientFreeText(displayName);
    if (parts.length > 1) {
      parts.forEach((part) => drafts.push(draftFromIngredientPhrase(part)));
      continue;
    }

    const draft = draftFromIngredientPhrase(displayName);
    if (percentage && !draft.amount) {
      draft.amount = percentage;
      draft.unit = draft.unit || '%';
    }
    drafts.push(draft);
  }

  return drafts;
}

function draftFromIngredientPhrase(part: string): FpBulkIngredientDraft {
  const text = String(part || '').trim();
  if (!text) return { name: '', amount: '', unit: '' };

  const pct = text.match(/^(.*?)\s+(\d+(?:\.\d+)?)\s*%\s*$/i);
  if (pct) {
    return { name: pct[1].trim(), amount: pct[2], unit: '%' };
  }

  const qty = text.match(
    /^(.*?)\s+(\d+(?:\.\d+)?)\s*(g|gm|grams?|kg|mg|ml|l|mcg|µg|ug)\s*$/i
  );
  if (qty) {
    return {
      name: qty[1].trim(),
      amount: qty[2],
      unit: normalizeUnitToken(qty[3]),
    };
  }

  return { name: text, amount: '', unit: '' };
}

function isJunkIngredientName(name: string): boolean {
  const n = String(name || '').trim().toLowerCase();
  if (!n) return true;
  if (/^pack\s*size\b/.test(n)) return true;
  if (
    /^(finish\s*product\s*id|sort\s*order|is\s*deleted|created\s*at|updated\s*at|is\s*active|percentage|id)\b/.test(
      n
    )
  ) {
    return true;
  }
  return false;
}

/**
 * If a single draft still holds a comma-separated label list, expand to one row each.
 * Keeps structured Name|Amount|Unit rows intact.
 */
function finalizeIngredientDrafts(drafts: FpBulkIngredientDraft[]): FpBulkIngredientDraft[] {
  const expanded: FpBulkIngredientDraft[] = [];

  for (const draft of drafts || []) {
    const name = String(draft?.name || '').trim();
    if (!name || isJunkIngredientName(name)) continue;

    const commaCount = (name.match(/,/g) || []).length;
    const looksLikeList =
      commaCount >= 1 &&
      !String(draft.amount || '').trim() &&
      name.length > 40;

    if (looksLikeList || commaCount >= 2) {
      const parts = splitIngredientFreeText(name);
      if (parts.length > 1) {
        parts.forEach((part) => {
          const next = draftFromIngredientPhrase(part);
          if (next.name && next.name.length > 1 && !isJunkIngredientName(next.name)) {
            expanded.push(next);
          }
        });
        continue;
      }
    }

    if (name.length > 1) {
      expanded.push({
        name,
        amount: String(draft.amount || '').trim(),
        unit: String(draft.unit || '').trim(),
      });
    }
  }

  return expanded;
}

/** Split free-text ingredient lists on , and ; while respecting parentheses. */
function splitIngredientFreeText(text: string): string[] {
  const cleaned = text.replace(/^ingredients?\s*[:\-]\s*/i, '').trim();
  const out: string[] = [];
  let buf = '';
  let depth = 0;
  for (let i = 0; i < cleaned.length; i++) {
    const ch = cleaned[i];
    if (ch === '(' || ch === '[') depth++;
    if (ch === ')' || ch === ']') depth = Math.max(0, depth - 1);
    if ((ch === ',' || ch === ';') && depth === 0) {
      const piece = buf.trim();
      if (piece) out.push(piece);
      buf = '';
      continue;
    }
    buf += ch;
  }
  const last = buf.trim();
  if (last) out.push(last);
  return out;
}

export function parseNutrientRaw(raw: string): FpBulkNutrientItem[] {
  const text = String(raw || '').trim();
  if (!text) return [];

  if (text.includes('|') || text.includes(';;')) {
    return text
    .split(';;')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const pieces = part.split('|').map((p) => p.trim());
      return {
        nutrientId: '',
        nutrientName: pieces[0] || '',
        unitId: '',
        unitName: pieces[1] || '',
        amount: pieces[2] || '',
        percentDV: pieces[3] || '',
        rda: pieces[4] || '',
      };
    })
    .filter((n) => n.nutrientName);
  }

  // Free text: "Per 100 g: Energy 454 kcal; Protein 6.9 g; …"
  const cleaned = text
    .replace(/^per\s+\d+(?:\.\d+)?\s*(g|kg|ml|l)\s*:\s*/i, '')
    .replace(/\.$/, '');
  const parts = cleaned.split(/[;,]|(?:\s+and\s+)/i).map((p) => p.trim()).filter(Boolean);
  const out: FpBulkNutrientItem[] = [];
  for (const part of parts) {
    const m = part.match(
      /^(.+?)\s+(\d+(?:\.\d+)?)\s*(g|gm|grams?|kg|mg|mcg|µg|ug|kcal|kj|iu|%)?\s*\.?\s*$/i
    );
    if (!m) continue;
    out.push({
      nutrientId: '',
      nutrientName: m[1].replace(/[:\-]+$/, '').trim(),
      unitId: '',
      unitName: normalizeUnitToken(m[3] || '') || (m[3] || ''),
      amount: m[2],
      percentDV: '',
      rda: '',
    });
  }
  return out.filter((n) => n.nutrientName && n.amount !== '');
}

/** Label-panel macros vs vitamins/minerals split for free-text nutrition cells. */
function isLikelyMacroNutrient(name: string): boolean {
  const n = String(name || '').trim().toLowerCase();
  if (!n) return false;
  if (
    /vitamin|mineral|calcium|iron|zinc|potassium|magnesium|phosphorus|iodine|choline|biotin|folic|niacin|copper|manganese|chloride|selenium|chromium|molybdenum|pantothen|riboflavin|thiamin|cobalamin|whey|casein/.test(
      n
    )
  ) {
    return false;
  }
  return /^(energy|calories?|kcal|protein|carbohydrates?|carbs?|total\s+sugars?|added\s+sugars?|sugars?|total\s+fat|saturated\s+fat|trans\s+fat|fat|cholesterol|sodium|salt|dietary\s+fib(er|re)|fib(er|re))\b/.test(
    n
  );
}

function partitionNutrients(items: FpBulkNutrientItem[]): {
  macros: FpBulkNutrientItem[];
  micros: FpBulkNutrientItem[];
} {
  const macros: FpBulkNutrientItem[] = [];
  const micros: FpBulkNutrientItem[] = [];
  (items || []).forEach((item) => {
    if (isLikelyMacroNutrient(item.nutrientName)) macros.push(item);
    else micros.push(item);
  });
  return { macros, micros };
}

function dedupeNutrientsByName(items: FpBulkNutrientItem[]): FpBulkNutrientItem[] {
  const seen = new Set<string>();
  const out: FpBulkNutrientItem[] = [];
  for (const item of items || []) {
    const key = String(item.nutrientName || '')
      .trim()
      .toLowerCase()
      .replace(/fibre/g, 'fiber');
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }
  return out;
}

function parseRowText(raw: Record<string, string>): FpBulkParsedText {
  return {
    companyName: cell(raw, 'CompanyName', 'Company Name'),
    address: cell(raw, 'Address'),
    executiveOwner: cell(raw, 'ExecutiveOwner', 'Executive Owner'),
    mobileNumber: cell(raw, 'MobileNumber', 'Mobile Number'),
    email: cell(raw, 'Email', 'EmailId', 'Email ID'),
    gstStatus: cell(raw, 'GstStatus', 'GST Status'),
    brandName: cell(raw, 'BrandName', 'Brand Name'),
    productName: cell(raw, 'ProductName', 'Product Name'),
    brandWebsite: cell(raw, 'BrandWebsite', 'Brand Website'),
    instagram: cell(raw, 'Instagram'),
    facebook: cell(raw, 'Facebook'),
    linkedin: cell(raw, 'LinkedIn', 'Linkedin', 'Linked In'),
    shelfLife: cell(raw, 'ShelfLife', 'Shelf Life'),
    netQuantity: cell(raw, 'NetQuantity', 'Net Quantity'),
    lotNumber: cell(raw, 'LotNumber', 'Lot Number'),
    referenceUrl: cell(raw, 'ReferenceUrl', 'Reference URL'),
    dataCapturingDate: cell(raw, 'DataCapturingDate', 'Data Capturing Date'),
    dateOfManufacturing: cell(raw, 'DateOfManufacturing', 'Date of Manufacturing'),
    dateOfExpiry: cell(raw, 'DateOfExpiry', 'Date of Expiry'),
    bestBeforeDate: cell(raw, 'BestBeforeDate', 'Best Before Date'),
    manufacturedByName: cell(raw, 'ManufacturedByName', 'Manufactured By Name'),
    manufacturedByAddress: cell(raw, 'ManufacturedByAddress', 'Manufactured By Address'),
    manufacturedByLicenseNo: cell(
      raw,
      'ManufacturedByLicenseNo',
      'Manufactured By License No',
      'ManufacturedByFSSAILicenseNo'
    ),
    marketedByName: cell(raw, 'MarketedByName', 'Marketed By Name'),
    marketedByAddress: cell(raw, 'MarketedByAddress', 'Marketed By Address'),
    marketedByLicenseNo: cell(raw, 'MarketedByLicenseNo', 'Marketed By License No'),
    packagedByName: cell(raw, 'PackagedByName', 'Packaged By Name'),
    packagedByAddress: cell(raw, 'PackagedByAddress', 'Packaged By Address'),
    packagedByLicenseNo: cell(raw, 'PackagedByLicenseNo', 'Packaged By License No'),
    importedByName: cell(raw, 'ImportedByName', 'Imported By Name'),
    importedByAddress: cell(raw, 'ImportedByAddress', 'Imported By Address'),
    importedByLicenseNo: cell(raw, 'ImportedByLicenseNo', 'Imported By License No'),
    exportedByName: cell(raw, 'ExportedByName', 'Exported By Name'),
    exportedByAddress: cell(raw, 'ExportedByAddress', 'Exported By Address'),
    exportedByLicenseNo: cell(raw, 'ExportedByLicenseNo', 'Exported By License No'),
    packagedBySection4Name:
      cell(raw, 'PackagedBySection4Name', 'Packaged By Section4 Name') ||
      cell(raw, 'PackagedByName', 'Packaged By Name'),
    packagedBySection4Address:
      cell(raw, 'PackagedBySection4Address') || cell(raw, 'PackagedByAddress', 'Packaged By Address'),
    packagedBySection4LicenseNo:
      cell(raw, 'PackagedBySection4LicenseNo') ||
      cell(raw, 'PackagedByLicenseNo', 'Packaged By License No'),
    country: cell(raw, 'Country', 'CompanyCountry', 'Country Name', 'CountryName'),
    state: cell(raw, 'State', 'CompanyState', 'State Name', 'StateName'),
    city: cell(raw, 'City', 'CompanyCity', 'City Name', 'CityName'),
    pincode: cell(raw, 'Pincode', 'PinCode', 'Pin Code', 'CompanyPincode'),
    healthColorCode: cell(
      raw,
      'HealthColorCode',
      'Health Color Code',
      'Health Color Code Name',
      'HealthColorCodeName'
    ),
    dietaryPreference: cell(raw, 'DietaryPreference', 'Dietary Preference'),
    productType: cell(raw, 'ProductType', 'Product Type'),
    productForm: cell(raw, 'ProductForm', 'Product Form'),
    dataSource: cell(raw, 'DataSource', 'Data Source'),
    databaseSource: cell(raw, 'DatabaseSource', 'Database Source'),
    countryOfOrigin: cell(raw, 'CountryOfOrigin', 'Country of Origin', 'Country Of Origin'),
    shelfLifeUnit: cell(raw, 'ShelfLifeUnit', 'Shelf Life Unit'),
    netQuantityUnit: cell(raw, 'NetQuantityUnit', 'Net Quantity Unit'),
    fssaiCategory: cell(raw, 'FssaiCategory', 'FSSAI Category'),
    fssaiSubCategory: cell(raw, 'FssaiSubCategory', 'FSSAI Sub Category'),
    fssaiSubSubCategory: cell(raw, 'FssaiSubSubCategory', 'FSSAI Sub Sub Category'),
    eStoreCategory: cell(raw, 'EStoreCategory', 'E-Store Category', 'E Store Category', 'EStore Category'),
    eStoreSubCategory: cell(raw, 'EStoreSubCategory', 'E Store Sub Category', 'E-Store Sub Category'),
    eStoreSubSubCategory: cell(
      raw,
      'EStoreSubSubCategory',
      'E Store Sub Sub Category',
      'E-Store Sub Sub Category'
    ),
    tastes: cell(raw, 'Tastes', 'Taste'),
    textures: cell(raw, 'Textures', 'Texture'),
    flavours: cell(raw, 'Flavours', 'Flavors', 'Flavour'),
    aromas: cell(raw, 'Aromas', 'Aroma'),
    continent: cell(raw, 'Continent'),
    region: cell(raw, 'Region'),
    subRegion: cell(raw, 'SubRegion', 'Sub Region'),
    cuisines: cell(raw, 'Cuisines', 'Cuisine'),
    occasions: cell(raw, 'Occasions', 'Occasion'),
    geoLocations: cell(raw, 'GeoLocations', 'Locations', 'Geo Locations'),
    seasons: cell(raw, 'Seasons', 'Season'),
    manufacturedByCountry: cell(raw, 'ManufacturedByCountry'),
    manufacturedByState: cell(raw, 'ManufacturedByState'),
    manufacturedByCity: cell(raw, 'ManufacturedByCity'),
    manufacturedByPincode: cell(raw, 'ManufacturedByPincode'),
    marketedByCountry: cell(raw, 'MarketedByCountry'),
    marketedByState: cell(raw, 'MarketedByState'),
    marketedByCity: cell(raw, 'MarketedByCity'),
    marketedByPincode: cell(raw, 'MarketedByPincode'),
    packagedByCountry: cell(raw, 'PackagedByCountry'),
    packagedByState: cell(raw, 'PackagedByState'),
    packagedByCity: cell(raw, 'PackagedByCity'),
    packagedByPincode: cell(raw, 'PackagedByPincode'),
    importedByCountry: cell(raw, 'ImportedByCountry', 'Imported By Country'),
    importedByState: cell(raw, 'ImportedByState', 'Imported By State'),
    importedByCity: cell(raw, 'ImportedByCity', 'Imported By City'),
    importedByPincode: cell(raw, 'ImportedByPincode', 'Imported By Pincode'),
    exportedByCountry: cell(raw, 'ExportedByCountry', 'Exported By Country'),
    exportedByState: cell(raw, 'ExportedByState', 'Exported By State'),
    exportedByCity: cell(raw, 'ExportedByCity', 'Exported By City'),
    exportedByPincode: cell(raw, 'ExportedByPincode', 'Exported By Pincode'),
    packagedBySection4Country: cell(raw, 'PackagedBySection4Country') || cell(raw, 'PackagedByCountry'),
    packagedBySection4State: cell(raw, 'PackagedBySection4State') || cell(raw, 'PackagedByState'),
    packagedBySection4City: cell(raw, 'PackagedBySection4City') || cell(raw, 'PackagedByCity'),
    packagedBySection4Pincode: cell(raw, 'PackagedBySection4Pincode') || cell(raw, 'PackagedByPincode'),
    skuRaw: cell(raw, 'SKUDetails', 'SkuDetails', 'SKU', 'Sku Details'),
    ingredientsRaw: cell(raw, 'Ingredients'),
    macrosRaw: cell(raw, 'Macros', 'Macronutrients'),
    microsRaw: cell(raw, 'Micronutrients', 'Micros'),
    servingMeasurementsRaw: cell(raw, 'ServingMeasurements', 'Serving Measurements', 'UOM'),
    allergenDeclared: cell(raw, 'AllergenDeclared', 'Allergens'),
    mayContain: cell(raw, 'MayContain'),
    producedInFacility: cell(raw, 'ProducedInFacility'),
    allergenDeclaredNotes: cell(raw, 'AllergenDeclaredNotes'),
    mayContainNotes: cell(raw, 'MayContainNotes'),
    producedInFacilityNotes: cell(raw, 'ProducedInFacilityNotes'),
    allergenContains: cell(raw, 'AllergenContains', 'Allergens Contains'),
    allergenFree: cell(raw, 'AllergenFree', 'Allergens Free'),
    intoleranceContains: cell(raw, 'IntoleranceContains', 'Intolerances'),
    intoleranceFree: cell(raw, 'IntoleranceFree', 'Intolerances Free'),
    productClaimsRaw: cell(raw, 'ProductClaims', 'Product Claims'),
    productBenefitsRaw: cell(raw, 'ProductBenefits', 'Product Benefits'),
    ingredientClaimsRaw: cell(raw, 'IngredientClaims', 'Ingredient Claims'),
    healthConditionTagsRaw: cell(raw, 'HealthConditionTags', 'Health Condition Tags'),
    reworkSuggestion: cell(raw, 'ReworkSuggestion', 'Rework Suggestion'),
    certificationRating: cell(raw, 'CertificationRating', 'Certification Rating'),
    status: cell(raw, 'Status'),
    directionForUse: cell(raw, 'DirectionForUse', 'Direction for Use'),
    warnings: cell(raw, 'Warnings'),
    disclaimer: cell(raw, 'Disclaimer'),
    reworkDescription: cell(raw, 'ReworkDescription'),
  };
}

function buildDisplay(parsed: FpBulkParsedText): Record<string, string> {
  return {
    BrandName: parsed.brandName,
    ProductName: parsed.productName,
    CompanyName: parsed.companyName,
  };
}

function toDateOrNull(value: string): string | null {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function toNumberOrNull(value: string): number | null {
  const n = Number(String(value || '').trim());
  return Number.isFinite(n) ? n : null;
}

function lookupName(
  options: Array<{ id: string | number; name: string }> | undefined,
  id: string | number | null | undefined
): string | null {
  if (id == null || id === '') return null;
  return findLookupById(options, id)?.name || null;
}

function mapSensory(
  ids: Array<string | number>,
  options: Array<{ id: string | number; name: string }>
): Array<{ id: string; name: string }> {
  return (ids || [])
    .map((id) => {
      const match = findLookupById(options, id);
      return match ? { id: String(match.id), name: match.name } : null;
    })
    .filter(Boolean) as Array<{ id: string; name: string }>;
}

function mapCuisineItems(
  ids: Array<string | number>,
  options: Array<{ id: string | number; name: string }>
): Array<{ id: string; name: string }> {
  return mapSensory(ids, options);
}

function mapAllergenItems(
  ids: Array<string | number>,
  options: Array<{ id: string | number; name: string }>
): Array<{ id: string; name: string; image: string; allergenType: number; description: string }> {
  return (ids || [])
    .map((id) => {
      const match = findLookupById(options, id);
      return match
        ? { id: String(match.id), name: match.name, image: '', allergenType: 0, description: '' }
        : null;
    })
    .filter(Boolean) as Array<{
    id: string;
    name: string;
    image: string;
    allergenType: number;
    description: string;
  }>;
}

function resolveLookupByFuzzyName(
  options: FpBulkLookupOption[] | undefined,
  name: string
): FpBulkLookupOption | null {
  if (!name || !options?.length) return null;
  const exact = findLookupByName(options, name);
  if (exact) return exact;
  const needle = name.trim().toLowerCase();
  return (
    options.find((o) => {
      const optionName = String(o.name || '').trim().toLowerCase();
      return optionName.includes(needle) || needle.includes(optionName);
    }) || null
  );
}

function normalizeNutrientLookupKey(name: string): string {
  return String(name || '')
    .toLowerCase()
    .replace(/fibre/g, 'fiber')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** Map bulk UI labels → common FSSAI nutrient master name fragments. */
const FP_MACRO_NAME_ALIASES: Record<string, string[]> = {
  energy: ['energy / calories', 'energy', 'calories', 'calorie', 'kcal'],
  protein: ['protein'],
  carbohydrates: ['carbohydrates', 'carbohydrate', 'total carbohydrate', 'total carbohydrates', 'carb', 'cho'],
  'total fat': ['total fat', 'fat', 'total fats'],
  'dietary fiber': ['dietary fiber', 'dietary fibre', 'fiber', 'fibre', 'total dietary fiber', 'total dietary fibre'],
  cholesterol: ['cholesterol'],
  sodium: ['sodium', 'salt'],
};

function resolveNutrientByAliases(
  nutrients: FpBulkLookupOption[],
  nutrientName: string
): FpBulkLookupOption | null {
  const key = normalizeNutrientLookupKey(nutrientName);
  if (!key) return null;

  const aliases = FP_MACRO_NAME_ALIASES[key] || [key];
  const list = nutrients || [];

  for (const alias of aliases) {
    const exact = list.find((o) => normalizeNutrientLookupKey(o.name) === alias);
    if (exact) return exact;
  }
  for (const alias of aliases) {
    const fuzzy = list.find((o) => {
      const n = normalizeNutrientLookupKey(o.name);
      return n.includes(alias) || alias.includes(n);
    });
    if (fuzzy) return fuzzy;
  }
  return resolveLookupByFuzzyName(list, nutrientName);
}

function resolveNutrientItems(
  items: FpBulkNutrientItem[],
  nutrients: FpBulkLookupOption[],
  units: FpBulkLookupOption[]
): FpBulkNutrientItem[] {
  return (items || [])
    .map((item) => {
      const byId = item.nutrientId ? findLookupById(nutrients, item.nutrientId) : null;
      const byName =
        !byId && item.nutrientName ? resolveNutrientByAliases(nutrients, item.nutrientName) : null;
      const nutrient = byId || byName;
      const unitById = item.unitId ? findLookupById(units, item.unitId) : null;
      const unitByName =
        !unitById && item.unitName ? resolveUnitLookup(units, item.unitName) : null;
      const unit = unitById || unitByName;
      return {
        ...item,
        nutrientId: nutrient ? String(nutrient.id) : String(item.nutrientId || ''),
        nutrientName: nutrient?.name || item.nutrientName || '',
        unitId: unit ? String(unit.id) : String(item.unitId || ''),
        unitName: unit?.name || item.unitName || '',
      };
    })
    // Keep name+amount rows even if master id not resolved yet — backend ResolveNutrientIdsAsync fills id.
    .filter((n) => {
      const hasId = !!String(n.nutrientId || '').trim();
      const hasName = !!String(n.nutrientName || '').trim();
      const hasAmount = n.amount !== '' && n.amount != null;
      return (hasId || hasName) && (hasName || hasAmount);
    });
}

function mapObservedItems(
  ids: Array<string | number>,
  options: Array<{ id: string | number; name: string }>
): Array<{ id: string; name: string; image: string; description: string }> {
  return (ids || [])
    .map((id) => {
      const match = findLookupById(options, id);
      return match ? { id: String(match.id), name: match.name, image: '', description: '' } : null;
    })
    .filter(Boolean) as Array<{ id: string; name: string; image: string; description: string }>;
}

function buildEntityPayload(
  name: string,
  address: string,
  licenseNo: string,
  sel: FpBulkEntitySelections
): Record<string, unknown> | null {
  const cleanName = sanitizeOptionalText(name, 500);
  const cleanAddress = sanitizeOptionalText(address, 2000);
  const cleanLicense = sanitizeOptionalText(licenseNo, 200);
  const cleanSel = entityIdClean(sel || createEmptyEntitySelections());
  // Skip rows that are only placeholders like "Not imported..."
  if (!cleanName && !cleanAddress && !cleanLicense && !cleanSel.countryId) return null;
  if (!cleanName && isPlaceholderText(name)) return null;
  return {
    name: cleanName || name || null,
    address: cleanAddress || null,
    licenseNo: cleanLicense || null,
    countryId: cleanSel.countryId,
    countryName: sanitizeOptionalText(sel?.countryName || '', 200),
    stateId: cleanSel.stateId,
    stateName: sanitizeOptionalText(sel?.stateName || '', 200),
    cityId: cleanSel.cityId,
    cityName: sanitizeOptionalText(sel?.cityName || '', 200),
    pincodeId: cleanSel.pincodeId,
    pincode: sanitizeOptionalText(sel?.pincode || '', 20),
    licenseImage: sel?.licenseImage || null,
  };
}

function categoryObj(id?: string | null, name?: string | null): { id: string; name: string } | null {
  const cleanId = numericIdOrNull(id);
  if (!cleanId) return null;
  return { id: cleanId, name: name || '' };
}

/** True when cell looks like a placeholder, not a real master id/value. */
function isPlaceholderText(value: string | null | undefined): boolean {
  const v = String(value || '').trim().toLowerCase();
  if (!v) return true;
  return (
    v.includes('not provided') ||
    v.includes('not stated') ||
    v.includes('not available') ||
    v.includes('not required') ||
    v.includes('not imported') ||
    v.includes('not separately') ||
    v.includes('master id') ||
    v.includes('public product') ||
    v.includes('source field') ||
    v.includes('batch-specific') ||
    v.includes('pack-specific') ||
    v.startsWith('no ') ||
    v === 'n/a' ||
    v === 'na' ||
    v === '-'
  );
}

function numericIdOrNull(value: string | number | null | undefined): string | null {
  if (value == null || value === '') return null;
  const raw = String(value).trim();
  if (!raw || isPlaceholderText(raw)) return null;
  if (/^\d+$/.test(raw)) return raw;
  return null;
}

function sanitizePhone(value: string): string | null {
  const raw = String(value || '').trim();
  if (!raw || isPlaceholderText(raw)) return null;
  const digits = raw.replace(/\D/g, '');
  if (digits.length >= 8 && digits.length <= 15) return digits;
  // Keep short numeric strings; drop long prose.
  if (raw.length <= 20 && /[\d+]/.test(raw)) return raw;
  return null;
}

function sanitizeEmail(value: string): string | null {
  const raw = String(value || '').trim();
  if (!raw || isPlaceholderText(raw)) return null;
  if (!raw.includes('@') || raw.length > 120) return null;
  return raw;
}

function sanitizeOptionalText(value: string, maxLen = 500): string | null {
  const raw = String(value || '').trim();
  if (!raw || isPlaceholderText(raw)) return null;
  return raw.length > maxLen ? raw.slice(0, maxLen) : raw;
}

function parseLeadingNumber(value: string): number | null {
  const m = String(value || '').trim().match(/^(\d+(?:\.\d+)?)/);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) ? n : null;
}

function extractUnitToken(value: string, allowed: string[]): string | null {
  const lower = String(value || '').toLowerCase();
  for (const u of allowed) {
    if (new RegExp(`\\b${u.toLowerCase()}\\b`, 'i').test(lower)) return u;
  }
  return null;
}

function entityIdClean(sel: FpBulkEntitySelections): FpBulkEntitySelections {
  return {
    ...sel,
    countryId: numericIdOrNull(sel.countryId),
    stateId: numericIdOrNull(sel.stateId),
    cityId: numericIdOrNull(sel.cityId),
    pincodeId: numericIdOrNull(sel.pincodeId),
  };
}

/** No field is required for finish-product bulk upload. */
export function getFpBulkFieldErrors(
  _parsed: FpBulkParsedText,
  _selections: FpBulkRowSelections
): FpBulkFieldError[] {
  return [];
}

function isRowConfigured(selections: FpBulkRowSelections): boolean {
  // Informational only — never blocks import.
  return !!(
    selections.countryId ||
    selections.healthColorCodeId ||
    selections.dietaryPreferenceId ||
    selections.productType ||
    selections.productForm ||
    selections.dataSourceId
  );
}

export function rebuildFpBulkPayload(row: FpBulkPreviewRow, lookups: FpBulkLookups): void {
  const { parsed, selections } = row;

  // Promote free-text ingredient drafts into saveable ingredient rows.
  if ((selections.ingredientDrafts || []).length && !(selections.ingredients || []).length) {
    selections.ingredients = selections.ingredientDrafts.map((d) => {
      const unitLabel = String(d.unit || '').trim();
      const cleanUnit = unitLabel && unitLabel !== '—' ? unitLabel : '';
      const resolved = cleanUnit ? resolveUnitLookup(lookups.units, cleanUnit) : null;
      return {
        ingredientId: '',
        name: d.name,
        servingUnitMasterId: resolved ? String(resolved.id) : '',
        unit: resolved?.name || cleanUnit || '—',
        amount: d.amount || '',
        isNew: true,
      };
    });
  } else if ((selections.ingredientDrafts || []).length && (selections.ingredients || []).length) {
    const existing = new Set(
      (selections.ingredients || []).map((i) => String(i.name || '').trim().toLowerCase())
    );
    selections.ingredientDrafts.forEach((d) => {
      const key = String(d.name || '').trim().toLowerCase();
      if (!key || existing.has(key)) return;
      const unitLabel = String(d.unit || '').trim();
      const cleanUnit = unitLabel && unitLabel !== '—' ? unitLabel : '';
      const resolved = cleanUnit ? resolveUnitLookup(lookups.units, cleanUnit) : null;
      selections.ingredients.push({
        ingredientId: '',
        name: d.name,
        servingUnitMasterId: resolved ? String(resolved.id) : '',
        unit: resolved?.name || cleanUnit || '—',
        amount: d.amount || '',
        isNew: true,
      });
      existing.add(key);
    });
  }

  // Resolve free-text ingredient units onto master unit IDs before payload build.
  (selections.ingredients || []).forEach((ing) => {
    const unitLabel = String(ing.unit || '').trim();
    const cleanUnit = unitLabel && unitLabel !== '—' ? unitLabel : '';
    const hasNumericUnit = /^\d+$/.test(String(ing.servingUnitMasterId || '').trim());
    if (!hasNumericUnit && cleanUnit) {
      const resolved = resolveUnitLookup(lookups.units, cleanUnit);
      if (resolved) {
        ing.servingUnitMasterId = String(resolved.id);
        ing.unit = resolved.name || cleanUnit;
      }
    }
  });

  // Keep product-level allergen / intolerance lists in sync with enriched ingredients.
  syncFpBulkAllergensFromIngredients(selections, lookups);

  // Drop non-numeric placeholder IDs that break FK / varchar columns.
  selections.countryId = numericIdOrNull(selections.countryId);
  selections.stateId = numericIdOrNull(selections.stateId);
  selections.cityId = numericIdOrNull(selections.cityId);
  selections.pincodeId = numericIdOrNull(selections.pincodeId);
  selections.healthColorCodeId = numericIdOrNull(selections.healthColorCodeId);
  selections.dietaryPreferenceId = numericIdOrNull(selections.dietaryPreferenceId as any);
  selections.dataSourceId = numericIdOrNull(selections.dataSourceId as any);
  selections.databaseSourceId = numericIdOrNull(selections.databaseSourceId as any);
  selections.countryOfOriginId = numericIdOrNull(selections.countryOfOriginId as any);
  selections.fssaiCategoryId = numericIdOrNull(selections.fssaiCategoryId);
  selections.fssaiSubCategoryId = numericIdOrNull(selections.fssaiSubCategoryId);
  selections.fssaiSubSubCategoryId = numericIdOrNull(selections.fssaiSubSubCategoryId);
  selections.eStoreCategoryId = numericIdOrNull(selections.eStoreCategoryId);
  selections.eStoreSubCategoryId = numericIdOrNull(selections.eStoreSubCategoryId);
  selections.eStoreSubSubCategoryId = numericIdOrNull(selections.eStoreSubSubCategoryId);
  selections.continentId = numericIdOrNull(selections.continentId as any);
  selections.regionId = numericIdOrNull(selections.regionId as any);
  selections.subRegionId = numericIdOrNull(selections.subRegionId as any);
  selections.reworkSuggestionId = numericIdOrNull(selections.reworkSuggestionId as any);
  selections.manufacturedBy = entityIdClean(selections.manufacturedBy);
  selections.marketedBy = entityIdClean(selections.marketedBy);
  selections.packagedBy = entityIdClean(selections.packagedBy);
  selections.importedBy = entityIdClean(selections.importedBy);
  selections.exportedBy = entityIdClean(selections.exportedBy);
  selections.packagedBySection4 = entityIdClean(selections.packagedBySection4);

  const typeOptions =
    lookups?.productTypes?.length > 0 ? lookups.productTypes : FP_BULK_PRODUCT_TYPES;
  const productType =
    matchEnumValue(selections.productType || parsed.productType, typeOptions) ||
    (!isPlaceholderText(parsed.productType) ? String(parsed.productType || '').trim() : null) ||
    selections.productType ||
    null;
  const productForm =
    matchEnumValue(selections.productForm || parsed.productForm, FP_BULK_PRODUCT_FORMS) ||
    (!isPlaceholderText(parsed.productForm) ? String(parsed.productForm || '').trim() : null) ||
    selections.productForm ||
    null;
  selections.productType = productType;
  selections.productForm = productForm;

  if (!selections.shelfLifeUnit) {
    const fromText =
      matchEnumValue(parsed.shelfLifeUnit, FP_BULK_SHELF_LIFE_UNITS) ||
      extractUnitToken(parsed.shelfLife, FP_BULK_SHELF_LIFE_UNITS);
    if (fromText) selections.shelfLifeUnit = fromText;
  }
  if (!selections.netQuantityUnit) {
    const fromText =
      matchEnumValue(parsed.netQuantityUnit, FP_BULK_NET_QTY_UNITS) ||
      extractUnitToken(parsed.netQuantity, FP_BULK_NET_QTY_UNITS) ||
      extractUnitToken(parsed.netQuantityUnit, FP_BULK_NET_QTY_UNITS);
    if (fromText) selections.netQuantityUnit = fromText;
  }

  const statusMatch = FP_BULK_STATUS_OPTIONS.find(
    (s) => s.toLowerCase() === String(selections.status || parsed.status || '').trim().toLowerCase()
  );
  // Keep free-text status from file when it doesn't match the workflow list.
  const rawStatus = String(parsed.status || selections.status || '').trim();
  selections.status =
    statusMatch ||
    (!isPlaceholderText(rawStatus) && rawStatus ? rawStatus : 'Draft');

  row.configured = isRowConfigured(selections);
  row.valid = true;
  row.error = undefined;

  const isAssignmentStatus =
    selections.status === 'Assigned to Maker' || selections.status === 'Assigned to Checker';

  const dietaryName =
    lookupName(lookups.dietaryPreferences, selections.dietaryPreferenceId) ||
    (!isPlaceholderText(parsed.dietaryPreference) ? parsed.dietaryPreference : null);
  const healthName = lookupName(lookups.healthColorCodes, selections.healthColorCodeId);
  const dataSourceName =
    lookupName(lookups.dataSources, selections.dataSourceId) ||
    (!isPlaceholderText(parsed.dataSource) ? parsed.dataSource : null);
  const databaseSourceName =
    lookupName(lookups.databaseSources, selections.databaseSourceId) ||
    (!isPlaceholderText(parsed.databaseSource) ? parsed.databaseSource : null);
  const countryOfOriginName =
    lookupName(lookups.countries, selections.countryOfOriginId) ||
    (!isPlaceholderText(parsed.countryOfOrigin) ? parsed.countryOfOrigin : null);

  const productName =
    (parsed.productName || '').trim() ||
    (parsed.brandName || '').trim() ||
    `Untitled Product (Row ${row.rowNumber})`;
  const brandName = (parsed.brandName || '').trim() || productName;
  const companyName = (parsed.companyName || '').trim() || brandName;

  const mobileNumber = sanitizePhone(parsed.mobileNumber);
  const emailId = sanitizeEmail(parsed.email);
  const gstStatus = sanitizeOptionalText(parsed.gstStatus, 100);
  const lotNumber = sanitizeOptionalText(parsed.lotNumber, 100);
  const shelfLife = parseLeadingNumber(parsed.shelfLife);
  const netQuantity = parseLeadingNumber(parsed.netQuantity);

  // Free-text allergens that didn't match master → keep in notes so data is not lost.
  const appendNote = (base: string, extra: string) => {
    const a = String(base || '').trim();
    const b = String(extra || '').trim();
    if (!b || isPlaceholderText(b)) return a;
    if (!a) return b;
    if (a.toLowerCase().includes(b.toLowerCase())) return a;
    return `${a}; ${b}`;
  };
  const allergenDeclaredNotes = appendNote(
    parsed.allergenDeclaredNotes,
    !(selections.allergenDeclaredIds || []).length ? parsed.allergenDeclared : ''
  );
  const mayContainNotes = appendNote(
    parsed.mayContainNotes,
    !(selections.mayContainIds || []).length ? parsed.mayContain : ''
  );
  const producedNotes = appendNote(
    parsed.producedInFacilityNotes,
    !(selections.producedInFacilityIds || []).length ? parsed.producedInFacility : ''
  );

  row.payload = {
    companyName,
    address: parsed.address,
    executiveOwner: sanitizeOptionalText(parsed.executiveOwner, 200) || parsed.executiveOwner || null,
    mobileNumber,
    emailId,
    gstStatus,
    countryId: selections.countryId,
    countryName: selections.countryName || lookupName(lookups.countries, selections.countryId),
    stateId: selections.stateId,
    stateName: sanitizeOptionalText(selections.stateName || parsed.state, 200),
    cityId: selections.cityId,
    cityName: sanitizeOptionalText(selections.cityName || parsed.city, 200),
    pincodeId: selections.pincodeId,
    pincode: sanitizeOptionalText(selections.pincode || parsed.pincode, 20),
    productType: selections.productType,
    productForm: selections.productForm,
    dietaryPreference: dietaryName,
    dietaryPreferenceName: dietaryName,
    dietaryPreferenceId: selections.dietaryPreferenceId != null ? String(selections.dietaryPreferenceId) : null,
    healthColorCode: healthName,
    healthColorCodeName: healthName,
    healthColorCodeId: selections.healthColorCodeId,
    brandName,
    productName,
    brandWebsite: sanitizeOptionalText(parsed.brandWebsite, 500),
    instagram: sanitizeOptionalText(parsed.instagram, 200),
    facebook: sanitizeOptionalText(parsed.facebook, 200),
    linkedin: sanitizeOptionalText(parsed.linkedin, 200),
    dataSource: dataSourceName,
    dataSourceName,
    dataSourceId: selections.dataSourceId != null ? String(selections.dataSourceId) : null,
    databaseSource: databaseSourceName,
    databaseSourceName,
    databaseSourceId: selections.databaseSourceId != null ? String(selections.databaseSourceId) : null,
    countryOfOrigin: countryOfOriginName,
    countryOfOriginId: selections.countryOfOriginId != null ? String(selections.countryOfOriginId) : null,
    shelfLife,
    shelfLifeUnit: selections.shelfLifeUnit || null,
    dataCapturingDate: toDateOrNull(parsed.dataCapturingDate),
    dateOfManufacturing: toDateOrNull(parsed.dateOfManufacturing),
    dateOfExpiry: toDateOrNull(parsed.dateOfExpiry),
    bestBeforeDate: toDateOrNull(parsed.bestBeforeDate),
    netQuantity,
    netQuantityUnit: selections.netQuantityUnit || null,
    lotNumber,
    referenceUrl: sanitizeOptionalText(parsed.referenceUrl, 1000),
    frontImage: selections.frontImage || null,
    backImage: selections.backImage || null,
    leftImage: selections.leftImage || null,
    rightImage: selections.rightImage || null,
    authorizedCertificateClaims: selections.authorizedCertificateImage
      ? [
          {
            authorizedCertificateId: '',
            authorizedCertificateName: '',
            certificateNumber: '',
            certificateImage: selections.authorizedCertificateImage,
          },
        ]
      : [],
    authorizedLicenseClaims: selections.authorizedLicenseImage
      ? [
          {
            authorizedLicenseId: '',
            authorizedLicenseName: '',
            licenseNumber: '',
            licenseImage: selections.authorizedLicenseImage,
          },
        ]
      : [],
    manufacturedBy: buildEntityPayload(
      parsed.manufacturedByName,
      parsed.manufacturedByAddress,
      parsed.manufacturedByLicenseNo,
      selections.manufacturedBy
    ),
    marketedBy: buildEntityPayload(
      parsed.marketedByName,
      parsed.marketedByAddress,
      parsed.marketedByLicenseNo,
      selections.marketedBy
    ),
    packagedBy: buildEntityPayload(
      parsed.packagedByName,
      parsed.packagedByAddress,
      parsed.packagedByLicenseNo,
      selections.packagedBy
    ),
    importedBy: buildEntityPayload(
      parsed.importedByName,
      parsed.importedByAddress,
      parsed.importedByLicenseNo,
      selections.importedBy
    ),
    exportedBy: buildEntityPayload(
      parsed.exportedByName,
      parsed.exportedByAddress,
      parsed.exportedByLicenseNo,
      selections.exportedBy
    ),
    packagedBySection4: buildEntityPayload(
      parsed.packagedBySection4Name,
      parsed.packagedBySection4Address,
      parsed.packagedBySection4LicenseNo,
      selections.packagedBySection4
    ),
    skuDetails: (selections.skuDetails || []).map((s) => ({
      name: s.name,
      netWeight: s.netWeight,
      unitId: s.unitId || null,
      unitName: s.unitName || null,
      countryId: s.countryId || null,
      countryName: s.countryName || null,
      productImage: s.productImage || null,
      productBarcode: s.productBarcode || null,
      mrp: s.mrp || null,
      manufacturedBy: s.manufacturedBy || null,
      marketedBy: s.marketedBy || null,
      packagedBy: s.packagedBy || null,
    })),
    servingMeasurements: (selections.servingMeasurements || []).map((sm) => {
      const macros = selections.macronutrients || [];
      const findAmt = (needle: string) => {
        const row = macros.find((m) =>
          String(m.nutrientName || '')
            .toLowerCase()
            .includes(needle)
        );
        const n = Number(row?.amount);
        return Number.isFinite(n) ? n : null;
      };
      const calories = Number(sm.calories) || findAmt('energy') || findAmt('calor') || 0;
      const carbs = Number(sm.carbs) || findAmt('carb') || 0;
      const protein = Number(sm.protein) || findAmt('protein') || 0;
      const fat = Number(sm.fat) || findAmt('fat') || 0;
      const fiber = Number(sm.fiber) || findAmt('fiber') || findAmt('fibre') || 0;
      return {
        ...sm,
        calories,
        carbs,
        protein,
        fat,
        fiber,
      };
    }),
    fssaiCategory: categoryObj(selections.fssaiCategoryId, selections.fssaiCategoryName),
    fssaiSubCategory: categoryObj(selections.fssaiSubCategoryId, selections.fssaiSubCategoryName),
    fssaiSubSubCategory: categoryObj(selections.fssaiSubSubCategoryId, selections.fssaiSubSubCategoryName),
    eStoreCategory: categoryObj(selections.eStoreCategoryId, selections.eStoreCategoryName),
    eStoreSubCategory: categoryObj(selections.eStoreSubCategoryId, selections.eStoreSubCategoryName),
    eStoreSubSubCategory: categoryObj(selections.eStoreSubSubCategoryId, selections.eStoreSubSubCategoryName),
    tastes: mapSensory(selections.tasteIds, lookups.tastes),
    textures: mapSensory(selections.textureIds, lookups.textures),
    flavours: mapSensory(selections.flavourIds, lookups.flavours),
    aromas: mapSensory(selections.aromaIds, lookups.aromas),
    continent: categoryObj(
      selections.continentId != null ? String(selections.continentId) : null,
      selections.continentName
    ),
    region: categoryObj(
      selections.regionId != null ? String(selections.regionId) : null,
      selections.regionName
    ),
    subRegion: categoryObj(
      selections.subRegionId != null ? String(selections.subRegionId) : null,
      selections.subRegionName
    ),
    cuisines: mapCuisineItems(selections.cuisineIds, lookups.cuisines),
    occasions: mapCuisineItems(selections.occasionIds, lookups.occasions),
    locations: mapCuisineItems(selections.locationIds, lookups.locations),
    seasons: mapCuisineItems(selections.seasonIds, lookups.seasons),
    ingredients: (selections.ingredients || []).map((i) => {
      const unitLabel = String(i.unit || '').trim();
      const cleanUnit = unitLabel && unitLabel !== '—' ? unitLabel : '';
      let servingUnitMasterId = String(i.servingUnitMasterId || '').trim();
      let resolvedUnitName = cleanUnit;
      if (!/^\d+$/.test(servingUnitMasterId) && cleanUnit) {
        const resolved = resolveUnitLookup(lookups.units, cleanUnit);
        if (resolved?.id != null && String(resolved.id).trim() !== '') {
          servingUnitMasterId = String(resolved.id);
          resolvedUnitName = resolved.name || cleanUnit;
        } else {
          servingUnitMasterId = '';
        }
      } else if (/^\d+$/.test(servingUnitMasterId) && !resolvedUnitName) {
        const byId = findLookupById(lookups.units, servingUnitMasterId);
        resolvedUnitName = byId?.name || '';
      }
      return {
      // FinishProductIngredients.IngredientRefId — never send IngredientMasters.Id here.
      ingredientId:
          /^\d+$/.test(String(i.ingredientRefId || '').trim())
            ? String(i.ingredientRefId).trim()
            : '',
        servingUnitMasterId,
      name: i.name,
      insNumber: i.insNumber || '',
      category: i.category || '',
      amount: i.amount,
        unit: resolvedUnitName || cleanUnit,
      isNew: !!i.isNew,
        faoFoodGroupName: i.faoFoodGroupName || '',
        allergenNames: i.allergenNames || '',
        allergenIds: Array.isArray(i.allergenIds) ? i.allergenIds : [],
        intoleranceNames: i.intoleranceNames || '',
        intoleranceIds: Array.isArray(i.intoleranceIds) ? i.intoleranceIds : [],
        allergenFreeIds: Array.isArray(i.allergenFreeIds) ? i.allergenFreeIds : [],
        intoleranceFreeIds: Array.isArray(i.intoleranceFreeIds) ? i.intoleranceFreeIds : [],
      };
    }),
    allergenInformation: {
      allergenDeclared: {
        allergens: mapAllergenItems(selections.allergenDeclaredIds, lookups.allergens),
        additionalInfo: allergenDeclaredNotes || '',
      },
      mayContain: {
        allergens: mapAllergenItems(selections.mayContainIds, lookups.allergens),
        additionalInfo: mayContainNotes || '',
      },
      producedInFacility: {
        allergens: mapAllergenItems(selections.producedInFacilityIds, lookups.allergens),
        additionalInfo: producedNotes || '',
      },
    },
    observedInformation: {
      allergenContains: { items: mapObservedItems(selections.allergenContainsIds, lookups.allergens) },
      allergenFree: { items: mapObservedItems(selections.allergenFreeIds, lookups.allergenFree) },
      intoleranceContains: {
        items: mapObservedItems(selections.intoleranceContainsIds, lookups.intolerances),
      },
      intoleranceFree: {
        items: mapObservedItems(selections.intoleranceFreeIds, lookups.intoleranceFree),
      },
    },
    nutrientInformation: {
      macronutrients: resolveNutrientItems(
        selections.macronutrients || [],
        lookups.nutrients || [],
        lookups.units || []
      ),
      micronutrients: resolveNutrientItems(
        selections.micronutrients?.length
          ? selections.micronutrients
          : parseNutrientRaw(parsed.microsRaw || ''),
        lookups.nutrients || [],
        lookups.units || []
      ),
    },
    healthConditionTags: selections.healthConditionTags || [],
    nutrientTags: {
      nutrientTags: selections.healthConditionTags || [],
    },
    generatedFssaiNutrientTags: selections.generatedFssaiNutrientTags || [],
    productClaims: (selections.productClaims || []).map((c) => ({
      claimTypeId: c.claimTypeId || '',
      claimTypeName: c.claimTypeName || '',
      claimText: c.claimText || '',
      selectedTagIds: c.selectedTagIds || [],
      selectedTagNames: c.selectedTagNames || [],
    })),
    productBenefitClaims: (selections.productBenefitClaims || []).map((c) => ({
      productBenefitId: c.claimTypeId || '',
      productBenefitName: c.claimTypeName || '',
      claimTypeId: c.claimTypeId || '',
      claimTypeName: c.claimTypeName || '',
      claimText: c.claimText || '',
      selectedTagIds: c.selectedTagIds || [],
      selectedTagNames: c.selectedTagNames || [],
    })),
    ingredientClaims: (selections.ingredientClaims || []).map((c) => ({
      claimTypeId: c.claimTypeId || '',
      claimTypeName: c.claimTypeName || '',
      claimText: c.claimText || '',
      selectedTagIds: c.selectedTagIds || [],
      selectedTagNames: c.selectedTagNames || [],
    })),
    reworkSuggestedClaims: selections.reworkSuggestionId
      ? [
          {
            reworkSuggestionId: String(selections.reworkSuggestionId),
            reworkSuggestionName: selections.reworkSuggestionName || '',
            description: parsed.reworkDescription || '',
          },
        ]
      : !isPlaceholderText(parsed.reworkDescription)
        ? [{ description: parsed.reworkDescription }]
        : !isPlaceholderText(parsed.reworkSuggestion)
          ? [{ description: parsed.reworkSuggestion }]
        : [],
    directionForUse: !isPlaceholderText(parsed.directionForUse)
      ? [{ directionText: parsed.directionForUse, description: parsed.directionForUse }]
      : [],
    warnings: !isPlaceholderText(parsed.warnings)
      ? [{ warningText: parsed.warnings, description: parsed.warnings }]
      : [],
    disclaimer: !isPlaceholderText(parsed.disclaimer)
      ? [{ disclaimerText: parsed.disclaimer, description: parsed.disclaimer }]
      : [],
    reworkCertification:
      selections.certificationRating != null && selections.certificationRating >= 1
        ? { rating: selections.certificationRating }
        : null,
    bulkImportNotes: buildBulkImportNotes(parsed, selections),
    status: isAssignmentStatus ? 'To be assigned' : selections.status || 'Draft',
  };
}

/** Persist free-text sensory/geo/etc. when master IDs could not be matched. */
function buildBulkImportNotes(
  parsed: FpBulkParsedText,
  selections: FpBulkRowSelections
): Array<{ sectionType: string; title: string; content: string }> {
  const notes: Array<{ sectionType: string; title: string; content: string }> = [];
  const push = (sectionType: string, title: string, content: string, hasIds: boolean) => {
    const text = String(content || '').trim();
    if (!text || isPlaceholderText(text)) return;
    if (hasIds) return;
    notes.push({ sectionType, title, content: text });
  };

  push('BulkTaste', 'Taste', parsed.tastes, !!(selections.tasteIds || []).length);
  push('BulkTexture', 'Texture', parsed.textures, !!(selections.textureIds || []).length);
  push('BulkFlavour', 'Flavour', parsed.flavours, !!(selections.flavourIds || []).length);
  push('BulkAroma', 'Aroma', parsed.aromas, !!(selections.aromaIds || []).length);
  push('BulkContinent', 'Continent', parsed.continent, !!selections.continentId);
  push('BulkRegion', 'Region', parsed.region, !!selections.regionId);
  push('BulkSubRegion', 'SubRegion', parsed.subRegion, !!selections.subRegionId);
  push('BulkCuisine', 'Cuisine', parsed.cuisines, !!(selections.cuisineIds || []).length);
  push('BulkOccasion', 'Occasion', parsed.occasions, !!(selections.occasionIds || []).length);
  push('BulkLocation', 'Location', parsed.geoLocations, !!(selections.locationIds || []).length);
  push('BulkSeason', 'Season', parsed.seasons, !!(selections.seasonIds || []).length);
  push(
    'BulkFssaiCategory',
    'FSSAI Category',
    parsed.fssaiCategory,
    !!selections.fssaiCategoryId
  );
  push(
    'BulkEStoreCategory',
    'E-Store Category',
    parsed.eStoreCategory,
    !!selections.eStoreCategoryId
  );
  push(
    'BulkHealthConditionTags',
    'Health Condition Tags',
    parsed.healthConditionTagsRaw,
    !!(selections.healthConditionTags || []).length
  );
  // Keep raw macros/micros text if structured parse found nothing useful
  if (!(selections.macronutrients || []).length) {
    push('BulkMacros', 'Macros', parsed.macrosRaw, false);
  }
  if (!(selections.micronutrients || []).length) {
    push('BulkMicronutrients', 'Micronutrients', parsed.microsRaw, false);
  }
  if (
    selections.certificationRating == null &&
    !isPlaceholderText(parsed.certificationRating)
  ) {
    push('BulkCertification', 'Certification Rating', parsed.certificationRating, false);
  }
  return notes;
}

export function refreshFpBulkParseCounts(result: FpBulkParseResult): void {
  result.validCount = result.rows.filter((r) => r.valid).length;
  result.errorCount = result.rows.length - result.validCount;
  result.totalCount = result.rows.length;
}

/** First sheet of .xlsx/.xls → same shape as parseCsvText. */
export function parseFpExcelArrayBuffer(buffer: ArrayBuffer): {
  headers: string[];
  rows: Record<string, string>[];
} {
  const workbook = XLSX.read(buffer, { type: 'array', cellDates: false });
  const sheetName = workbook.SheetNames?.[0];
  if (!sheetName) return { headers: [], rows: [] };

  const sheet = workbook.Sheets[sheetName];
  const aoa = XLSX.utils.sheet_to_json<(string | number | boolean | null | undefined)[]>(sheet, {
    header: 1,
    defval: '',
    raw: false,
  });
  if (!aoa?.length) return { headers: [], rows: [] };

  const headers = (aoa[0] || []).map((h) => String(h ?? '').trim());
  if (!headers.some((h) => h.length > 0)) return { headers: [], rows: [] };

  const rows = aoa
    .slice(1)
    .filter((values) => (values || []).some((c) => String(c ?? '').trim().length > 0))
    .map((values) => {
      const record: Record<string, string> = {};
      headers.forEach((header, i) => {
        if (!header) return;
        record[header] = String(values?.[i] ?? '').trim();
      });
      return record;
    });

  return { headers, rows };
}

function buildFpPreviewRows(rows: Record<string, string>[]): FpBulkPreviewRow[] {
  return rows.map((raw, index) => {
    const parsed = parseRowText(raw);
    const display = buildDisplay(parsed);
    const selections = createDefaultFpBulkSelections();
    selections.ingredientDrafts = parseIngredientsRaw(parsed.ingredientsRaw);
    selections.skuDetails = parseSkuRaw(parsed.skuRaw);
    // Fallback unit / net weight from product-level NetQuantity* columns
    (selections.skuDetails || []).forEach((sku) => {
      if (!sku.unitName) {
        const fromCol = normalizeUnitToken(parsed.netQuantityUnit);
        const fromQty = extractQtyAndUnit(parsed.netQuantity);
        sku.unitName = fromCol || fromQty?.unit || null;
      }
      if (!sku.netWeight) {
        const fromQty = extractQtyAndUnit(parsed.netQuantity);
        if (fromQty) sku.netWeight = fromQty.amount;
        else if (parsed.netQuantity) sku.netWeight = String(parsed.netQuantity).trim();
      }
      if (sku.unitName) sku.unitName = normalizeUnitToken(sku.unitName);
    });
    // If SKUDetails empty but NetQuantity present, still create one SKU row
    if (!(selections.skuDetails || []).length && (parsed.netQuantity || parsed.netQuantityUnit || parsed.productName)) {
      const fromQty = extractQtyAndUnit(parsed.netQuantity) || extractQtyAndUnit(`${parsed.netQuantity} ${parsed.netQuantityUnit}`);
      selections.skuDetails = [
        {
          name: parsed.productName || parsed.brandName || 'SKU 1',
          netWeight: fromQty?.amount || String(parsed.netQuantity || '').trim(),
          mrp: '',
          manufacturedBy: '',
          marketedBy: '',
          packagedBy: '',
          unitId: null,
          unitName: normalizeUnitToken(parsed.netQuantityUnit) || fromQty?.unit || null,
          countryId: null,
          countryName: null,
          productImage: null,
          productBarcode: null,
        },
      ];
    }
    const fromMacrosCol = partitionNutrients(parseNutrientRaw(parsed.macrosRaw));
    const fromMicrosCol = partitionNutrients(parseNutrientRaw(parsed.microsRaw));
    selections.macronutrients = dedupeNutrientsByName([
      ...fromMacrosCol.macros,
      ...fromMicrosCol.macros,
    ]);
    selections.micronutrients = dedupeNutrientsByName([
      ...fromMacrosCol.micros,
      ...fromMicrosCol.micros,
    ]);
    selections.servingMeasurements = parseServingMeasurementsRaw(parsed.servingMeasurementsRaw);
    selections.productClaims = parseClaimsRaw(parsed.productClaimsRaw);
    selections.productBenefitClaims = parseClaimsRaw(parsed.productBenefitsRaw);
    selections.ingredientClaims = parseClaimsRaw(parsed.ingredientClaimsRaw);
    if (parsed.status) {
      const matched = FP_BULK_STATUS_OPTIONS.find(
        (s) => s.toLowerCase() === parsed.status.trim().toLowerCase()
      );
      if (matched) selections.status = matched;
    }
    const rating = Number(parsed.certificationRating);
    if (Number.isFinite(rating) && rating >= 1 && rating <= 5) {
      selections.certificationRating = rating;
    }

    applyExportManufacturerColumns(raw, parsed, selections);
    applyExportSkuColumns(raw, selections);

    return {
      rowNumber: index + 1,
      valid: true,
      configured: false,
      error: undefined,
      cells: raw,
      display,
      parsed,
      selections,
    };
  });
}

export function validateFpBulkRows(
  headers: string[],
  rows: Record<string, string>[]
): FpBulkParseResult {
  if (!headers.length) {
    return {
      rows: [],
      validCount: 0,
      errorCount: 0,
      totalCount: 0,
      fileError: 'File is empty or missing headers.',
    };
  }

  if (rows.length > FP_BULK_MAX_ROWS) {
    return {
      rows: [],
      validCount: 0,
      errorCount: 0,
      totalCount: rows.length,
      fileError: `File exceeds the limit of ${FP_BULK_MAX_ROWS} rows (${rows.length} found). Please split the file (same cap as recipe bulk upload).`,
    };
  }

  const previewRows = buildFpPreviewRows(rows);
  return {
    rows: previewRows,
    validCount: previewRows.length,
    errorCount: 0,
    totalCount: previewRows.length,
  };
}

export function validateFpBulkCsv(csvText: string): FpBulkParseResult {
  const { headers, rows } = parseCsvText(csvText);
  return validateFpBulkRows(headers, rows);
}

export function validateFpBulkExcel(buffer: ArrayBuffer): FpBulkParseResult {
  const { headers, rows } = parseFpExcelArrayBuffer(buffer);
  return validateFpBulkRows(headers, rows);
}

function extractAllergenTokens(
  raw: string,
  options: FpBulkLookupOption[] | undefined
): Array<string | number> {
  if (!raw || isPlaceholderText(raw) || !options?.length) return [];
  const lower = raw.toLowerCase();
  const hits: Array<string | number> = [];
  for (const opt of options) {
    const name = String(opt.name || '').trim();
    if (!name || name.length < 3) continue;
    const needle = name.toLowerCase();
    if (lower.includes(needle)) hits.push(opt.id);
  }
  return [...new Set(hits.map(String))].map((id) => {
    const match = options.find((o) => String(o.id) === id);
    return match ? match.id : id;
  });
}

function matchEnumValue(value: string, options: string[]): string | null {
  const needle = String(value || '').trim().toLowerCase();
  if (!needle) return null;
  return options.find((o) => o.toLowerCase() === needle) || null;
}

function splitMasterTokens(raw: string): string[] {
  return String(raw || '')
    .split(/[;|,]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Resolve tokens that may be master IDs, names, or a mix. */
function resolveMasterTokens(
  options: Array<{ id: string | number; name: string; label?: string; colorCode?: string }> | undefined,
  raw: string
): Array<string | number> {
  const tokens = splitMasterTokens(raw);
  if (!tokens.length) return [];
  const out: Array<string | number> = [];
  for (const token of tokens) {
    const byId = options?.length ? findLookupById(options as any, token) : null;
    if (byId) {
      out.push(byId.id);
      continue;
    }
    if (/^\d+$/.test(token) && (!options || !options.length)) {
      out.push(token);
      continue;
    }
    if (/^\d+$/.test(token)) continue;
    if (isPlaceholderText(token)) continue;
    const byName = findLookupByNameFlexible(options, token);
    if (byName) out.push(byName.id);
  }
  const seen = new Set<string>();
  return out.filter((id) => {
    const key = String(id);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function findLookupByNameFlexible(
  options: Array<{ id: string | number; name: string; label?: string; colorCode?: string }> | undefined,
  name: string
): { id: string | number; name: string } | null {
  const needle = String(name || '').trim().toLowerCase();
  if (!needle || !options?.length) return null;
  const exact =
    options.find((o) => String(o.name || '').trim().toLowerCase() === needle) ||
    options.find((o) => String(o.label || '').trim().toLowerCase() === needle) ||
    options.find(
      (o) => String(o.colorCode || '').replace(/\s/g, '').toLowerCase() === needle.replace(/\s/g, '')
    );
  if (exact) return exact;

  // Avoid "veg" fuzzy-matching "Vegan" / "Non Veg" (includes is too loose for diet labels).
  const normalizedNeedle = needle.replace(/[-_]/g, ' ').replace(/\s+/g, ' ').trim();
  const isDietToken =
    normalizedNeedle === 'veg' ||
    normalizedNeedle === 'vegan' ||
    normalizedNeedle === 'vegetarian' ||
    normalizedNeedle === 'non veg' ||
    normalizedNeedle === 'nonveg' ||
    normalizedNeedle === 'egg' ||
    normalizedNeedle === 'eggetarian';
  if (isDietToken) {
    const dietAliases: Record<string, string[]> = {
      veg: ['veg', 'vegetarian'],
      vegetarian: ['veg', 'vegetarian'],
      vegan: ['vegan'],
      'non veg': ['non veg', 'non-veg', 'nonveg'],
      nonveg: ['non veg', 'non-veg', 'nonveg'],
      egg: ['egg', 'eggetarian'],
      eggetarian: ['egg', 'eggetarian'],
    };
    const aliases = dietAliases[normalizedNeedle] || [normalizedNeedle];
    const dietHit = options.find((o) => {
      const n = String(o.name || '')
        .trim()
        .toLowerCase()
        .replace(/[-_]/g, ' ')
        .replace(/\s+/g, ' ');
      return aliases.some((a) => n === a);
    });
    if (dietHit) return dietHit;
    return null;
  }

  return (
    options.find((o) => String(o.name || '').trim().toLowerCase().includes(needle)) ||
    options.find((o) => needle.includes(String(o.name || '').trim().toLowerCase())) ||
    null
  );
}

/** Map Excel unit tokens (g, ml, %) onto Unit master names (Gm, Ml, Percent, …). */
function resolveUnitLookup(
  units: Array<{ id: string | number; name: string }> | undefined,
  unitName: string | null | undefined
): { id: string | number; name: string } | null {
  if (!units?.length || !unitName) return null;
  const token = normalizeUnitToken(unitName);
  if (!token) return null;

  const aliasGroups: Record<string, string[]> = {
    g: ['gm', 'g', 'gram', 'grams'],
    kg: ['kg', 'kilogram', 'kilograms'],
    ml: ['ml', 'milliliter', 'millilitre', 'milliliters', 'millilitres'],
    L: ['l', 'ltr', 'liter', 'litre', 'liters', 'litres'],
    mg: ['mg', 'milligram', 'milligrams'],
    mcg: ['mcg', 'µg', 'ug', 'microgram', 'micrograms'],
    '%': ['percent', '%', 'percentage', 'pct'],
    kcal: ['kcal', 'kilocalorie', 'kilocalories', 'calorie', 'calories'],
    kj: ['kj', 'kilojoule', 'kilojoules'],
    IU: ['iu'],
  };
  const aliases = aliasGroups[token] || [token.toLowerCase()];

  for (const alias of aliases) {
    const hit = units.find((o) => String(o.name || '').trim().toLowerCase() === alias);
    if (hit) return hit;
  }
  // Prefer Gm over G/dl when token is g
  if (token === 'g') {
    const gm = units.find((o) => /^gm$/i.test(String(o.name || '').trim()));
    if (gm) return gm;
  }
  if (token === '%') {
    const pct = units.find((o) => /percent/i.test(String(o.name || '').trim()));
    if (pct) return pct;
  }
  if (token === 'kcal') {
    const kcal = units.find((o) => /^kcal$/i.test(String(o.name || '').trim()));
    if (kcal) return kcal;
  }
  return findLookupByNameFlexible(units, token);
}

function hydrateMultiFromFile(
  cells: Record<string, string>,
  options: Array<{ id: string | number; name: string; label?: string; colorCode?: string }> | undefined,
  idAliases: string[],
  nameAliases: string[]
): Array<string | number> {
  const combined = [cell(cells, ...idAliases), cell(cells, ...nameAliases)]
    .filter((v) => !!String(v || '').trim())
    .join(',');
  return resolveMasterTokens(options, combined);
}

function hydrateSingleFromFile(
  cells: Record<string, string>,
  options: Array<{ id: string | number; name: string; label?: string; colorCode?: string }> | undefined,
  idAliases: string[],
  nameAliases: string[]
): string | number | null {
  const ids = hydrateMultiFromFile(cells, options, idAliases, nameAliases);
  return ids.length ? ids[0] : null;
}

type EntityKey =
  | 'manufacturedBy'
  | 'marketedBy'
  | 'packagedBy'
  | 'importedBy'
  | 'exportedBy'
  | 'packagedBySection4';

function manufacturerTypeToKey(type: string): EntityKey | null {
  const t = String(type || '')
    .replace(/[\s_-]+/g, '')
    .toLowerCase();
  if (t === 'manufacturedby') return 'manufacturedBy';
  if (t === 'marketedby') return 'marketedBy';
  if (t === 'packagedby') return 'packagedBy';
  if (t === 'importedby') return 'importedBy';
  if (t === 'exportedby') return 'exportedBy';
  if (t === 'packagedbysection4' || t === 'packagedby4') return 'packagedBySection4';
  return null;
}

function setParsedEntityFields(
  parsed: FpBulkParsedText,
  key: EntityKey,
  name: string,
  address: string,
  license: string
): void {
  if (key === 'manufacturedBy') {
    if (name) parsed.manufacturedByName = name;
    if (address) parsed.manufacturedByAddress = address;
    if (license) parsed.manufacturedByLicenseNo = license;
  } else if (key === 'marketedBy') {
    if (name) parsed.marketedByName = name;
    if (address) parsed.marketedByAddress = address;
    if (license) parsed.marketedByLicenseNo = license;
  } else if (key === 'packagedBy') {
    if (name) parsed.packagedByName = name;
    if (address) parsed.packagedByAddress = address;
    if (license) parsed.packagedByLicenseNo = license;
  } else if (key === 'importedBy') {
    if (name) parsed.importedByName = name;
    if (address) parsed.importedByAddress = address;
    if (license) parsed.importedByLicenseNo = license;
  } else if (key === 'exportedBy') {
    if (name) parsed.exportedByName = name;
    if (address) parsed.exportedByAddress = address;
    if (license) parsed.exportedByLicenseNo = license;
  } else if (key === 'packagedBySection4') {
    if (name) parsed.packagedBySection4Name = name;
    if (address) parsed.packagedBySection4Address = address;
    if (license) parsed.packagedBySection4LicenseNo = license;
  }
}

/** Map full-export "Manufacturers N *" columns into entity text + location IDs. */
function applyExportManufacturerColumns(
  cells: Record<string, string>,
  parsed: FpBulkParsedText,
  selections: FpBulkRowSelections
): void {
  for (let i = 1; i <= 12; i++) {
    const type = cell(cells, `Manufacturers ${i} Type`, `Manufacturer ${i} Type`);
    const name = cell(cells, `Manufacturers ${i} Name`, `Manufacturer ${i} Name`);
    const address = cell(cells, `Manufacturers ${i} Address`, `Manufacturer ${i} Address`);
    const license = cell(
      cells,
      `Manufacturers ${i} FSSAI License No`,
      `Manufacturers ${i} License No`,
      `Manufacturer ${i} FSSAI License No`
    );
    if (!type && !name && !address && !license) continue;
    const key = manufacturerTypeToKey(type) || (i === 1 ? 'manufacturedBy' : null);
    if (!key) continue;
    setParsedEntityFields(parsed, key, name, address, license);
    const sel = selections[key];
    const countryId = cell(cells, `Manufacturers ${i} Country ID`, `Manufacturers ${i} CountryId`);
    const stateId = cell(cells, `Manufacturers ${i} State ID`, `Manufacturers ${i} StateId`);
    const cityId = cell(cells, `Manufacturers ${i} City ID`, `Manufacturers ${i} CityId`);
    const pincodeId = cell(cells, `Manufacturers ${i} Pincode ID`, `Manufacturers ${i} PincodeId`);
    if (countryId) sel.countryId = numericIdOrNull(countryId);
    if (stateId) sel.stateId = numericIdOrNull(stateId);
    if (cityId) sel.cityId = numericIdOrNull(cityId);
    if (pincodeId) sel.pincodeId = numericIdOrNull(pincodeId);
    const licenseImage = cell(
      cells,
      `Manufacturers ${i} License Image URL`,
      `Manufacturers ${i} License Image`
    );
    if (licenseImage) sel.licenseImage = licenseImage;
  }
}

/** Map full-export "Sku Details N *" columns into skuDetails drafts. */
function applyExportSkuColumns(cells: Record<string, string>, selections: FpBulkRowSelections): void {
  const fromExport: FpBulkSkuDraft[] = [];
  for (let i = 1; i <= 20; i++) {
    const name = cell(
      cells,
      `Sku Details ${i} Sku Code`,
      `SKU Details ${i} Sku Code`,
      `Sku Details ${i} Name`
    );
    const mrp = cell(cells, `Sku Details ${i} Mrp`, `SKU Details ${i} MRP`, `Sku Details ${i} MRP`);
    const netWeight = cell(
      cells,
      `Sku Details ${i} Net Quantity`,
      `SKU Details ${i} Net Quantity`
    );
    const barcode = cell(cells, `Sku Details ${i} Barcode`, `SKU Details ${i} Barcode`);
    const unitId = cell(
      cells,
      `Sku Details ${i} Net Quantity Unit ID`,
      `SKU Details ${i} Net Quantity Unit ID`
    );
    const unitName = cell(
      cells,
      `Sku Details ${i} Net Quantity Unit`,
      `SKU Details ${i} Unit`,
      `Sku Details ${i} Net Quantity Unit Name`
    );
    const countryId = cell(cells, `Sku Details ${i} Country ID`, `SKU Details ${i} Country ID`);
    const image = cell(
      cells,
      `Sku Details ${i} Product Image URL`,
      `SKU Details ${i} Product Image URL`
    );
    const manufacturedBy = cell(
      cells,
      `Sku Details ${i} Manufactured By Label`,
      `SKU Details ${i} Manufactured By`
    );
    const marketedBy = cell(
      cells,
      `Sku Details ${i} Marketed By Label`,
      `SKU Details ${i} Marketed By`
    );
    const packagedBy = cell(
      cells,
      `Sku Details ${i} Packaged By Label`,
      `SKU Details ${i} Packaged By`
    );
    if (!name && !mrp && !netWeight && !barcode && !unitId && !unitName) continue;
    fromExport.push(
      enrichSkuDraftFromText({
        name: name || `SKU ${i}`,
        netWeight: netWeight || '',
        mrp: mrp || '',
        manufacturedBy: manufacturedBy || '',
        marketedBy: marketedBy || '',
        packagedBy: packagedBy || '',
        unitId: unitId || null,
        unitName: unitName ? normalizeUnitToken(unitName) : null,
        countryId: countryId || null,
        countryName: null,
        productImage: image || null,
        productBarcode: barcode || null,
      })
    );
  }
  if (fromExport.length) {
    selections.skuDetails = fromExport;
  }
}

function applyEntityLocationHints(
  sel: FpBulkEntitySelections,
  country: string,
  state: string,
  city: string,
  pincode: string,
  countries: FpBulkLookupOption[],
  cells?: Record<string, string>,
  idPrefix?: string
): void {
  const countryIdHint = idPrefix
    ? cell(cells || {}, `${idPrefix}CountryId`, `${idPrefix} Country ID`, `${idPrefix}Country ID`)
    : '';
  if (countryIdHint || country) {
    const match =
      findLookupById(countries, countryIdHint) || findLookupByNameFlexible(countries, country);
    if (match) {
      sel.countryId = String(match.id);
      sel.countryName = match.name;
    } else if (country) {
      sel.countryName = country;
    } else if (countryIdHint) {
      sel.countryId = countryIdHint;
    }
  }
  const stateIdHint = idPrefix
    ? cell(cells || {}, `${idPrefix}StateId`, `${idPrefix} State ID`)
    : '';
  if (stateIdHint) sel.stateId = stateIdHint;
  if (state) sel.stateName = state;
  const cityIdHint = idPrefix ? cell(cells || {}, `${idPrefix}CityId`, `${idPrefix} City ID`) : '';
  if (cityIdHint) sel.cityId = cityIdHint;
  if (city) sel.cityName = city;
  const pinIdHint = idPrefix
    ? cell(cells || {}, `${idPrefix}PincodeId`, `${idPrefix} Pincode ID`)
    : '';
  if (pinIdHint) sel.pincodeId = pinIdHint;
  if (pincode) sel.pincode = pincode;
}

function applyCategoryPair(
  cells: Record<string, string>,
  options: FpBulkLookupOption[] | undefined,
  idAliases: string[],
  nameAliases: string[],
  setIds: (id: string, name: string) => void
): void {
  const id = hydrateSingleFromFile(cells, options, idAliases, nameAliases);
  if (id == null) return;
  const match = findLookupById(options, id) || findLookupByNameFlexible(options, String(id));
  if (match) setIds(String(match.id), match.name);
  else setIds(String(id), cell(cells, ...nameAliases) || '');
}

/** Map CSV/XLSX name + ID columns onto dropdown IDs using loaded lookups. */
export function applyFpCsvMasterMappings(row: FpBulkPreviewRow, lookups: FpBulkLookups): void {
  const p = row.parsed;
  const s = row.selections;
  const cells = row.cells || {};

  applyExportManufacturerColumns(cells, p, s);
  if (!(s.skuDetails || []).some((x) => x.name || x.mrp || x.netWeight)) {
    applyExportSkuColumns(cells, s);
  }

  const typeOptions =
    lookups?.productTypes?.length > 0 ? lookups.productTypes : FP_BULK_PRODUCT_TYPES;
  const productType = matchEnumValue(p.productType, typeOptions);
  if (productType) s.productType = productType;

  const productForm = matchEnumValue(p.productForm, FP_BULK_PRODUCT_FORMS);
  if (productForm) s.productForm = productForm;

  const shelfUnit = matchEnumValue(p.shelfLifeUnit, FP_BULK_SHELF_LIFE_UNITS);
  if (shelfUnit) s.shelfLifeUnit = shelfUnit;

  const netUnit = matchEnumValue(p.netQuantityUnit, FP_BULK_NET_QTY_UNITS);
  if (netUnit) s.netQuantityUnit = netUnit;

  const healthId = hydrateSingleFromFile(
    cells,
    lookups.healthColorCodes,
    ['HealthColorCodeId', 'Health Color Code ID'],
    ['HealthColorCode', 'Health Color Code', 'Health Color Code Name', 'HealthColorCodeName']
  );
  if (healthId != null) s.healthColorCodeId = String(healthId);

  const dietaryId = hydrateSingleFromFile(
    cells,
    lookups.dietaryPreferences,
    ['DietaryPreferenceId', 'Dietary Preference ID'],
    ['DietaryPreference', 'Dietary Preference']
  );
  if (dietaryId != null) s.dietaryPreferenceId = dietaryId;

  const dataSourceId = hydrateSingleFromFile(
    cells,
    lookups.dataSources,
    ['DataSourceId', 'Data Source ID'],
    ['DataSource', 'Data Source']
  );
  if (dataSourceId != null) s.dataSourceId = dataSourceId;

  const databaseSourceId = hydrateSingleFromFile(
    cells,
    lookups.databaseSources,
    ['DatabaseSourceId', 'Database Source ID'],
    ['DatabaseSource', 'Database Source']
  );
  if (databaseSourceId != null) s.databaseSourceId = databaseSourceId;

  const originId = hydrateSingleFromFile(
    cells,
    lookups.countries,
    ['CountryOfOriginId', 'Country of Origin ID', 'Country Of Origin ID'],
    ['CountryOfOrigin', 'Country of Origin', 'Country Of Origin']
  );
  if (originId != null) s.countryOfOriginId = originId;

  applyCategoryPair(
    cells,
    lookups.fssaiCategories,
    ['FssaiCategoryId', 'FSSAI Category ID'],
    ['FssaiCategory', 'FSSAI Category'],
    (id, name) => {
      s.fssaiCategoryId = id;
      s.fssaiCategoryName = name;
    }
  );
  applyCategoryPair(
    cells,
    lookups.fssaiCategories,
    ['FssaiSubCategoryId', 'FSSAI Sub Category ID'],
    ['FssaiSubCategory', 'FSSAI Sub Category'],
    (id, name) => {
      s.fssaiSubCategoryId = id;
      s.fssaiSubCategoryName = name;
    }
  );
  applyCategoryPair(
    cells,
    lookups.fssaiCategories,
    ['FssaiSubSubCategoryId', 'FSSAI Sub Sub Category ID'],
    ['FssaiSubSubCategory', 'FSSAI Sub Sub Category'],
    (id, name) => {
      s.fssaiSubSubCategoryId = id;
      s.fssaiSubSubCategoryName = name;
    }
  );
  applyCategoryPair(
    cells,
    lookups.eStoreCategories,
    ['EStoreCategoryId', 'E Store Category ID', 'E-Store Category ID'],
    ['EStoreCategory', 'E Store Category', 'E-Store Category'],
    (id, name) => {
      s.eStoreCategoryId = id;
      s.eStoreCategoryName = name;
    }
  );
  applyCategoryPair(
    cells,
    lookups.eStoreCategories,
    ['EStoreSubCategoryId', 'E Store Sub Category ID'],
    ['EStoreSubCategory', 'E Store Sub Category'],
    (id, name) => {
      s.eStoreSubCategoryId = id;
      s.eStoreSubCategoryName = name;
    }
  );
  applyCategoryPair(
    cells,
    lookups.eStoreCategories,
    ['EStoreSubSubCategoryId', 'E Store Sub Sub Category ID'],
    ['EStoreSubSubCategory', 'E Store Sub Sub Category'],
    (id, name) => {
      s.eStoreSubSubCategoryId = id;
      s.eStoreSubSubCategoryName = name;
    }
  );

  const countryId = hydrateSingleFromFile(
    cells,
    lookups.countries,
    ['CountryId', 'Country ID'],
    ['Country', 'Country Name', 'CompanyCountry']
  );
  if (countryId != null) {
    s.countryId = String(countryId);
    s.countryName = findLookupById(lookups.countries, countryId)?.name || p.country || s.countryName;
  } else if (p.country) {
      s.countryName = p.country;
  }
  if (p.state) s.stateName = p.state;
  if (p.city) s.cityName = p.city;
  if (p.pincode) s.pincode = p.pincode;
  const stateIdHint = cell(cells, 'StateId', 'State ID');
  if (numericIdOrNull(stateIdHint)) s.stateId = numericIdOrNull(stateIdHint);
  const cityIdHint = cell(cells, 'CityId', 'City ID');
  if (numericIdOrNull(cityIdHint)) s.cityId = numericIdOrNull(cityIdHint);
  const pincodeIdHint = cell(cells, 'PincodeId', 'Pincode ID');
  if (numericIdOrNull(pincodeIdHint)) s.pincodeId = pincodeIdHint;

  applyEntityLocationHints(
    s.manufacturedBy,
    p.manufacturedByCountry,
    p.manufacturedByState,
    p.manufacturedByCity,
    p.manufacturedByPincode,
    lookups.countries,
    cells,
    'ManufacturedBy'
  );
  applyEntityLocationHints(
    s.marketedBy,
    p.marketedByCountry,
    p.marketedByState,
    p.marketedByCity,
    p.marketedByPincode,
    lookups.countries,
    cells,
    'MarketedBy'
  );
  applyEntityLocationHints(
    s.packagedBy,
    p.packagedByCountry,
    p.packagedByState,
    p.packagedByCity,
    p.packagedByPincode,
    lookups.countries,
    cells,
    'PackagedBy'
  );
  applyEntityLocationHints(
    s.importedBy,
    p.importedByCountry,
    p.importedByState,
    p.importedByCity,
    p.importedByPincode,
    lookups.countries,
    cells,
    'ImportedBy'
  );
  applyEntityLocationHints(
    s.exportedBy,
    p.exportedByCountry,
    p.exportedByState,
    p.exportedByCity,
    p.exportedByPincode,
    lookups.countries,
    cells,
    'ExportedBy'
  );
  applyEntityLocationHints(
    s.packagedBySection4,
    p.packagedBySection4Country,
    p.packagedBySection4State,
    p.packagedBySection4City,
    p.packagedBySection4Pincode,
    lookups.countries,
    cells,
    'PackagedBySection4'
  );

  const tastes = hydrateMultiFromFile(
    cells,
    lookups.tastes,
    ['TasteIds', 'Taste Ids', 'TasteId'],
    ['Tastes', 'Taste']
  );
  if (tastes.length) s.tasteIds = tastes;
  const textures = hydrateMultiFromFile(
    cells,
    lookups.textures,
    ['TextureIds', 'Texture Ids'],
    ['Textures', 'Texture']
  );
  if (textures.length) s.textureIds = textures;
  const flavours = hydrateMultiFromFile(
    cells,
    lookups.flavours,
    ['FlavourIds', 'FlavorIds', 'Flavour Ids'],
    ['Flavours', 'Flavors', 'Flavour']
  );
  if (flavours.length) s.flavourIds = flavours;
  const aromas = hydrateMultiFromFile(
    cells,
    lookups.aromas,
    ['AromaIds', 'Aroma Ids'],
    ['Aromas', 'Aroma']
  );
  if (aromas.length) s.aromaIds = aromas;
  const cuisines = hydrateMultiFromFile(
    cells,
    lookups.cuisines,
    ['CuisineIds', 'Cuisine Ids'],
    ['Cuisines', 'Cuisine']
  );
  if (cuisines.length) s.cuisineIds = cuisines;
  const occasions = hydrateMultiFromFile(
    cells,
    lookups.occasions,
    ['OccasionIds', 'Occasion Ids'],
    ['Occasions', 'Occasion']
  );
  if (occasions.length) s.occasionIds = occasions;
  const locations = hydrateMultiFromFile(
    cells,
    lookups.locations,
    ['GeoLocationIds', 'LocationIds', 'Geo Location Ids'],
    ['GeoLocations', 'Locations', 'Geo Locations']
  );
  if (locations.length) s.locationIds = locations;
  const seasons = hydrateMultiFromFile(
    cells,
    lookups.seasons,
    ['SeasonIds', 'Season Ids'],
    ['Seasons', 'Season']
  );
  if (seasons.length) s.seasonIds = seasons;

  const allergenDeclared = hydrateMultiFromFile(
    cells,
    lookups.allergens,
    ['AllergenDeclaredIds', 'Allergen Declared Ids'],
    ['AllergenDeclared', 'Allergens']
  );
  if (allergenDeclared.length) s.allergenDeclaredIds = allergenDeclared;
  else {
    const extracted = extractAllergenTokens(p.allergenDeclared, lookups.allergens);
    if (extracted.length) s.allergenDeclaredIds = extracted;
  }
  const mayContain = hydrateMultiFromFile(
    cells,
    lookups.allergens,
    ['MayContainIds', 'May Contain Ids'],
    ['MayContain']
  );
  if (mayContain.length) s.mayContainIds = mayContain;
  else {
    const extracted = extractAllergenTokens(p.mayContain, lookups.allergens);
    if (extracted.length) s.mayContainIds = extracted;
  }
  const produced = hydrateMultiFromFile(
    cells,
    lookups.allergens,
    ['ProducedInFacilityIds', 'Produced In Facility Ids'],
    ['ProducedInFacility']
  );
  if (produced.length) s.producedInFacilityIds = produced;
  else {
    const extracted = extractAllergenTokens(p.producedInFacility, lookups.allergens);
    if (extracted.length) s.producedInFacilityIds = extracted;
  }
  const allergenContains = hydrateMultiFromFile(
    cells,
    lookups.allergens,
    ['AllergenContainsIds', 'Allergen Contains Ids'],
    ['AllergenContains', 'Allergens Contains']
  );
  if (allergenContains.length) s.allergenContainsIds = allergenContains;
  else {
    const extracted = extractAllergenTokens(p.allergenContains, lookups.allergens);
    if (extracted.length) s.allergenContainsIds = extracted;
  }
  const allergenFree = hydrateMultiFromFile(
    cells,
    lookups.allergenFree,
    ['AllergenFreeIds', 'Allergen Free Ids'],
    ['AllergenFree', 'Allergens Free']
  );
  if (allergenFree.length) s.allergenFreeIds = allergenFree;
  const intoleranceContains = hydrateMultiFromFile(
    cells,
    lookups.intolerances,
    ['IntoleranceContainsIds', 'Intolerance Contains Ids'],
    ['IntoleranceContains', 'Intolerances']
  );
  if (intoleranceContains.length) s.intoleranceContainsIds = intoleranceContains;
  const intoleranceFree = hydrateMultiFromFile(
    cells,
    lookups.intoleranceFree,
    ['IntoleranceFreeIds', 'Intolerance Free Ids'],
    ['IntoleranceFree', 'Intolerances Free']
  );
  if (intoleranceFree.length) s.intoleranceFreeIds = intoleranceFree;

  s.macronutrients = resolveNutrientItems(
    s.macronutrients?.length ? s.macronutrients : partitionNutrients(parseNutrientRaw(p.macrosRaw)).macros,
    lookups.nutrients || [],
    lookups.units || []
  );
  {
    const fromCols = partitionNutrients([
      ...parseNutrientRaw(p.macrosRaw),
      ...parseNutrientRaw(p.microsRaw),
    ]);
    const microSource = s.micronutrients?.length
      ? s.micronutrients
      : dedupeNutrientsByName([...fromCols.micros]);
    s.micronutrients = resolveNutrientItems(
      microSource,
      lookups.nutrients || [],
      lookups.units || []
    );
    if (!(s.macronutrients || []).length) {
      s.macronutrients = resolveNutrientItems(
        dedupeNutrientsByName(fromCols.macros),
        lookups.nutrients || [],
        lookups.units || []
      );
    }
  }

  const continentId = hydrateSingleFromFile(
    cells,
    lookups.continents,
    ['ContinentId', 'Continent ID'],
    ['Continent']
  );
  if (continentId != null) {
    s.continentId = continentId;
    s.continentName = findLookupById(lookups.continents, continentId)?.name || p.continent || null;
  }

  const regionId = hydrateSingleFromFile(cells, lookups.continents, ['RegionId', 'Region ID'], [
    'Region',
  ]);
  // regions may live under continents tree; keep raw id/name if present
  if (regionId != null) {
    s.regionId = regionId;
    s.regionName = p.region || null;
  } else if (p.region) {
    s.regionName = p.region;
  }
  if (p.subRegion) s.subRegionName = p.subRegion;
  const subRegionId = cell(cells, 'SubRegionId', 'Sub Region ID');
  if (subRegionId) s.subRegionId = subRegionId;

  // Resolve SKU unit / country by id or name (g → Gm, ml → Ml, % → Percent, …)
  (s.skuDetails || []).forEach((sku) => {
    if (!sku.unitName && p.netQuantityUnit) {
      sku.unitName = normalizeUnitToken(p.netQuantityUnit);
    }
    if (!sku.unitName) {
      const fromQty = extractQtyAndUnit(p.netQuantity) || extractQtyAndUnit(sku.netWeight) || extractQtyAndUnit(sku.name);
      if (fromQty) {
        sku.unitName = fromQty.unit;
        if (!sku.netWeight) sku.netWeight = fromQty.amount;
      }
    }
    if (sku.unitId) {
      const u = findLookupById(lookups.units, sku.unitId);
      if (u) sku.unitName = u.name;
    } else if (sku.unitName) {
      const u = resolveUnitLookup(lookups.units, sku.unitName);
      if (u) {
        sku.unitId = String(u.id);
        sku.unitName = u.name;
      }
    }
    if (sku.countryId) {
      const c = findLookupById(lookups.countries, sku.countryId);
      if (c) sku.countryName = c.name;
    } else if (sku.countryName) {
      const c = findLookupByNameFlexible(lookups.countries, sku.countryName);
      if (c) {
        sku.countryId = String(c.id);
        sku.countryName = c.name;
      }
    }
  });

  // Resolve claim / benefit category + tags from free-text claim lines
  const resolveClaimDrafts = (
    claims: FpBulkClaimDraft[],
    types: Array<{ id: string | number; name: string }> | undefined,
    tags: Array<{ id: string | number; name: string; claimTypeId?: any; productBenefitId?: any }> | undefined,
    parentKey: 'claimTypeId' | 'productBenefitId'
  ) => {
    (claims || []).forEach((c) => {
      if (c.claimTypeId) {
        const t = findLookupById(types, c.claimTypeId);
        if (t) c.claimTypeName = t.name;
        return;
      }

      const candidates = [c.claimTypeName, c.claimText]
        .map((x) => String(x || '').trim())
        .filter((x) => x && !/^imported$/i.test(x));

      for (const cand of candidates) {
        const typeHit = findLookupByNameFlexible(types, cand);
        if (typeHit) {
          c.claimTypeId = String(typeHit.id);
          c.claimTypeName = typeHit.name;
          break;
        }
        const tagHit = findLookupByNameFlexible(tags as any, cand);
        if (tagHit) {
          if (!(c.selectedTagIds || []).map(String).includes(String(tagHit.id))) {
            c.selectedTagIds = [...(c.selectedTagIds || []), String(tagHit.id)];
            c.selectedTagNames = [...(c.selectedTagNames || []), tagHit.name];
          }
          const parentId = (tagHit as any)[parentKey];
          if (parentId != null && parentId !== '') {
            c.claimTypeId = String(parentId);
            const parent = findLookupById(types, parentId);
            if (parent) c.claimTypeName = parent.name;
          }
          break;
        }
      }

      // Loose tag contains match (e.g. "gluten free" ↔ "Gluten Free")
      if (!c.claimTypeId && c.claimText && tags?.length) {
        const needle = c.claimText.toLowerCase();
        const tagHit = tags.find((t) => {
          const n = String(t.name || '').trim().toLowerCase();
          return n.length >= 4 && (needle.includes(n) || n.includes(needle));
        });
        if (tagHit) {
          c.selectedTagIds = [...(c.selectedTagIds || []), String(tagHit.id)];
          c.selectedTagNames = [...(c.selectedTagNames || []), tagHit.name];
          const parentId = (tagHit as any)[parentKey];
          if (parentId != null && parentId !== '') {
            c.claimTypeId = String(parentId);
            const parent = findLookupById(types, parentId);
            if (parent) c.claimTypeName = parent.name;
          }
        }
      }

      // Still no category — keep free-text claim; pick a sensible default type if master has one
      if (!c.claimTypeId && types?.length) {
        const fallback =
          findLookupByNameFlexible(types, 'Other') ||
          findLookupByNameFlexible(types, 'General') ||
          findLookupByNameFlexible(types, 'Product Claim') ||
          findLookupByNameFlexible(types, 'Nutrition Claim') ||
          findLookupByNameFlexible(types, 'Benefit') ||
          types[0];
        if (fallback) {
          c.claimTypeId = String(fallback.id);
          c.claimTypeName = fallback.name;
        }
      }
    });
  };

  resolveClaimDrafts(s.productClaims, lookups.claimTypes, lookups.claimTags, 'claimTypeId');
  resolveClaimDrafts(
    s.productBenefitClaims,
    lookups.productBenefits,
    lookups.productBenefitTags,
    'productBenefitId'
  );
  resolveClaimDrafts(s.ingredientClaims, lookups.claimTypes, lookups.claimTags, 'claimTypeId');

  if (p.healthConditionTagsRaw) {
    const tags = hydrateMultiFromFile(
      cells,
      lookups.nutrientTags,
      ['HealthConditionTagIds', 'Health Condition Tag Ids'],
      ['HealthConditionTags', 'Health Condition Tags']
    );
    if (tags.length) {
      s.healthConditionTags = [
        {
          groupId: '',
          groupName: 'Imported',
          tags: tags.map((id) => {
            const m = findLookupById(lookups.nutrientTags, id);
            return { id: String(id), name: m?.name || String(id) };
          }),
        },
      ];
    } else if (!(s.healthConditionTags || []).length) {
      const parts = String(p.healthConditionTagsRaw || '')
        .split(/[;|]/)
        .map((x) => x.trim().replace(/\.+$/, ''))
        .filter((x) => x.length > 2 && !isPlaceholderText(x));
      if (parts.length) {
        s.healthConditionTags = [
          {
            groupId: 'from-file',
            groupName: 'From file',
            tags: parts.map((name, i) => ({ id: `from-file-${i}`, name })),
          },
        ];
      }
    }
  }

  const reworkId = hydrateSingleFromFile(
    cells,
    lookups.reworkSuggestions,
    ['ReworkSuggestionId', 'Rework Suggestion ID'],
    ['ReworkSuggestion', 'Rework Suggestion']
  );
  if (reworkId != null) {
    s.reworkSuggestionId = reworkId;
    s.reworkSuggestionName =
      findLookupById(lookups.reworkSuggestions, reworkId)?.name || p.reworkSuggestion || null;
  }

  if (p.status) {
    const matched = FP_BULK_STATUS_OPTIONS.find(
      (st) => st.toLowerCase() === p.status.trim().toLowerCase()
    );
    if (matched) s.status = matched;
  }

  // Images from export URLs
  const front = cell(cells, 'FrontImage', 'Front Image');
  const back = cell(cells, 'BackImage', 'Back Image');
  const left = cell(cells, 'LeftImage', 'Left Image');
  const right = cell(cells, 'RightImage', 'Right Image');
  if (front) s.frontImage = front;
  if (back) s.backImage = back;
  if (left) s.leftImage = left;
  if (right) s.rightImage = right;
}

export function applyFpCsvMasterMappingsToRows(rows: FpBulkPreviewRow[], lookups: FpBulkLookups): void {
  (rows || []).forEach((row) => applyFpCsvMasterMappings(row, lookups));
}

export function mapFpMetadataToLookups(meta: any, countryMeta: any, recipeMeta?: any): FpBulkLookups {
  const root = meta?.response ?? meta?.data ?? meta ?? {};
  const countryRoot = countryMeta?.response ?? countryMeta?.data ?? countryMeta ?? {};
  const recipeRoot = recipeMeta?.response ?? recipeMeta?.data ?? recipeMeta ?? {};

  const mapHealth = (list: any[]): FpBulkLookups['healthColorCodes'] =>
    (list || [])
      .map((item) => ({
        id: String(item.id ?? item.Id ?? ''),
        name: String(item.name ?? item.Name ?? item.label ?? item.Label ?? item.title ?? item.Title ?? '').trim(),
        label: String(item.label ?? item.Label ?? item.title ?? item.Title ?? '').trim(),
        colorCode: String(item.colorCode ?? item.ColorCode ?? item.color ?? '').trim(),
      }))
      .filter((item) => item.id && item.name);

  const mapOpts = (list: any[]): FpBulkLookups['dataSources'] =>
    extractMetadataOptions(list)
      .map((o) => ({ id: o.id, name: o.name }))
      .filter((o) => o.id != null && o.id !== '' && !!o.name);

  const countries =
    countryRoot.countries ||
    countryRoot.Countries ||
    countryRoot.items ||
    (Array.isArray(countryRoot) ? countryRoot : []);

  return {
    healthColorCodes: mapHealth(root.healthColorCodes || root.HealthColorCodes),
    dietaryPreferences: mapOpts(
      recipeRoot.dietaryPreferences || recipeRoot.DietaryPreferences || root.dietaryPreferences
    ),
    dataSources: mapOpts(root.ingredientItemTypes || root.IngredientItemTypes || root.dataSources),
    databaseSources: mapOpts(root.recipeLinkSources || root.RecipeLinkSources || root.databaseSources),
    countries: mapOpts(Array.isArray(countries) ? countries : []),
    tastes: mapOpts(recipeRoot.tastes || recipeRoot.Tastes || root.tastes),
    textures: mapOpts(recipeRoot.textures || recipeRoot.Textures || root.textures),
    flavours: mapOpts(recipeRoot.flavours || recipeRoot.Flavours || root.flavours),
    aromas: mapOpts(recipeRoot.aromas || recipeRoot.Aromas || root.aromas),
    continents: mapOpts(recipeRoot.cuisineContinents || recipeRoot.CuisineContinents || root.continents),
    occasions: mapOpts(recipeRoot.occasions || recipeRoot.Occasions || root.occasions),
    locations: mapOpts(recipeRoot.locations || recipeRoot.Locations || root.locations),
    seasons: mapOpts(recipeRoot.seasons || recipeRoot.Seasons || root.seasons),
    cuisines: mapOpts(recipeRoot.cuisines || recipeRoot.Cuisines || root.cuisines),
    allergens: mapOpts(recipeRoot.allergens || recipeRoot.Allergens || root.allergens),
    allergenFree: mapOpts(recipeRoot.allergenFree || recipeRoot.AllergenFree || root.allergenFree),
    intolerances: mapOpts(recipeRoot.intolerances || recipeRoot.Intolerances || root.intolerances),
    intoleranceFree: mapOpts(
      recipeRoot.intoleranceFree || recipeRoot.IntoleranceFree || root.intoleranceFree
    ),
    fssaiCategories: mapOpts(root.fssaiCategories || root.FssaiCategories || []),
    eStoreCategories: mapOpts(root.eStoreCategories || root.EStoreCategories || []),
    units: mapOpts(root.units || root.Units || root.servingUnits || []),
    reworkSuggestions: mapOpts(root.reworkSuggestions || root.ReworkSuggested || root.reworkSuggested || []),
    nutrients: mapOpts(root.fssaiNutrients || root.FssaiNutrients || root.nutrients || root.Nutrients || []),
    nutrientGroups: mapOpts(root.nutrientGroups || root.NutrientGroups || []),
    nutrientTags: (Array.isArray(root.nutrientTags || root.NutrientTags) ? root.nutrientTags || root.NutrientTags : [])
      .map((item: any) => ({
        id: item?.id ?? item?.Id ?? '',
        name: String(item?.name ?? item?.Name ?? item?.label ?? item?.Label ?? '').trim(),
        groupId: item?.groupId ?? item?.GroupId ?? item?.parentId ?? item?.ParentId ?? item?.nutrientGroupId ?? null,
      }))
      .filter((o: any) => o.id !== '' && !!o.name),
    claimTypes: mapOpts(root.claimTypes || root.ClaimTypes || []),
    claimTags: (Array.isArray(root.claimTags || root.ClaimTags) ? root.claimTags || root.ClaimTags : [])
      .map((item: any) => ({
        id: item?.id ?? item?.Id ?? '',
        name: String(item?.name ?? item?.Name ?? item?.label ?? item?.Label ?? '').trim(),
        claimTypeId: item?.claimTypeId ?? item?.ClaimTypeId ?? item?.parentId ?? item?.ParentId ?? null,
      }))
      .filter((o: any) => o.id !== '' && !!o.name),
    productBenefits: mapOpts(root.productBenefits || root.ProductBenefits || []),
    productBenefitTags: (Array.isArray(root.productBenefitTags || root.ProductBenefitTags)
      ? root.productBenefitTags || root.ProductBenefitTags
      : []
    )
      .map((item: any) => ({
        id: item?.id ?? item?.Id ?? '',
        name: String(item?.name ?? item?.Name ?? item?.label ?? item?.Label ?? '').trim(),
        productBenefitId:
          item?.productBenefitId ?? item?.ProductBenefitId ?? item?.parentId ?? item?.ParentId ?? null,
      }))
      .filter((o: any) => o.id !== '' && !!o.name),
    productTypes: (() => {
      const names: string[] = (Array.isArray(root.productTypes || root.ProductTypes)
        ? root.productTypes || root.ProductTypes
        : []
      )
        .map((item: any) => String(item?.name ?? item?.Name ?? item?.label ?? item?.Label ?? '').trim())
        .filter((name: string): name is string => !!name);
      return names.length ? Array.from(new Set<string>(names)) : [...FP_BULK_PRODUCT_TYPES];
    })(),
  };
}

export function applyGlobalStatusToFpRows(rows: FpBulkPreviewRow[], status: string): void {
  rows.forEach((row) => {
    row.selections.status = status;
    if (status !== 'Assigned to Maker' && status !== 'Assigned to Checker') {
      row.selections.assignment = null;
    }
  });
}
